/* eslint-disable react/prop-types */
import React from 'react';
import '../../styles/table.css';
import '../../App.css';
import { Box, Card, Stack, Tooltip, Typography, TextField } from '@mui/material';
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_Cell,
  type MRT_ColumnDef,
  type MRT_Row,
  type MRT_RowSelectionState,
  type MRT_TableInstance,
  type MRT_VisibilityState,
  type MRT_SortingState,
  MRT_TablePagination,
} from 'material-react-table';
import { LineItemDTO, ProjectData, ProjectLineItemDTO, ProjectLineItemsAndSummary, ProjectLineItemSubtotalDTO } from './project.type';
import {
  computeSubtotalDisplayData,
  computeSubtotalReanchors,
  SubtotalLabelDialog,
  useSubtotalActions,
  useSubtotalRangeEditor,
  SubtotalRangeApplyResult,
} from './subtotal.util';
import { LineItemLabelEnum, LineItemLabel } from '../glossary/glossary.type';
import { toast } from 'react-toastify';
import { formatCurrency, formatPercentage, parseCurrency } from '../../common/utils/common.util';
import { NEGATIVE_NUMBER_COLOR, SUBTOTAL_ROW_BACKGROUND_COLOR } from '../../common/constants/general';
import { useQueryClient } from 'react-query';
import { AutoAwesome, Check, Close, Delete, Edit, Info, InfoOutlined, List, MoreVert } from '@mui/icons-material';
import EditDrawLineItemDialog from './project-draws/EditDrawLineItemDialog';
import { CreateDrawLineItemDTO, CreateOrUpdateProjectDrawDTO } from './project-draws/project-draws.type';

import { handleError } from 'common/utils/util.errors';
import { usePrompt } from '../AppPrompt';
import WhiteFab from '../common/WhiteFab';
import { compareArrays } from '../report/report.util';
import SelectDivisionFromGiven from '../glossary/SelectDivisionFromGiven';
import DescriptionGlossaryField from './project-draws/DescriptionGlossaryField';
import { useLineItemSearch } from '../../hooks/useLineItemSearch';
import BulkEditDrawLineItemsDialog from './project-draws/BulkEditDrawLineItemsDialog';
import AddChangeOrdersDialog from './project-draws/AddChangeOrdersDialog';
import { Division, LineItemForPrediction, LineItemUnit } from '../glossary/glossary.type';
import { REPORT_COLORS } from '../report/constants';
import { ROUTES } from '../../common/constants/urls';
import LegendItem from '../general/Legend';
import UploadDialog from '../file-upload/UploadDialog';
import { friendlyFieldNames } from '../file-upload/Payapp.util';
import { getPayappDraft, getPayappFileUrl } from '../payapp-process/PayappDraft.api';
import { useNavigate } from 'react-router-dom';
import { backendToLabelMap } from '../file-upload/Payapp.util';
import { payappStepIds } from '../../pages/PayappProcess';
import {
  numericColumns,
  calculateBalanceToComplete,
  calculateCdccApproved,
  calculatePercentageComplete,
  calculateRevisedBudget,
  calculateTotalCompletedStored,
  getNextApplicationNumber,
  getNextChangeOrderDescriptions,
  getNextSerialNumber,
  isChangedFromLastPeriod,
  NO_PAYAPPS_UPLOADED_TOOLTIP,
  START_NEXT_REPORT_TOOLTIP,
} from './project.util';
import { useMRTStyling } from '../../hooks/useMRTStyling';
import { buildSelectAllCheckboxProps } from '../common/mrtSelectAllHelpers';
import { useExcelEditing, excelSelectedCellSx } from '../../hooks/useExcelEditing';
import {
  applySubtotalRangeChanges,
  getReorderRowAttributes,
  RowDragHandle,
  RowReorderResult,
  useRowReorderDrop,
  useScrollToRow,
} from './rowReorder.util';
import { IconOverTextButton } from '../common/IconOverTextButton';
import NumericFormatInput from '../general/NumericFormatInput';
import {
  Add as AddIcon,
  CloudUpload,
  Description,
  Fullscreen,
  FullscreenExit,
  Search,
  Functions,
  Undo,
  Star,
  StarBorder,
  PostAdd,
} from '@mui/icons-material';

const DEFAULT_PAGE_SIZE = 100;
const MAX_UNDO_HISTORY = 40;
const EDIT_FLAG_FIELDS = ['isEdited', 'isCreated'] as const;

interface ProjectTableProps {
  projectId: string;
  projectNumber?: string;
  projectName?: string;
  projectDrawId: string;
  projectDrawName: string;
  drawInputRetainagePercentage?: number;
  onDrawInputRetainagePercentageChange?: (field: string, value: any, target: 'drawSummary' | 'budgetSummary') => void;
  inputRetainagePercentageHasError?: boolean;
  lineItems: ProjectLineItemDTO[];
  payappDraftId?: string;
  openAutoAssignDialog?: (lineItemsToAutoAssign: LineItemForPrediction[]) => void;
  predictingDivisions: boolean;
  divisionsData: Division[];
  isLoading?: boolean;
  updateProject: (updatedProject: Partial<ProjectData>, isEditMode: boolean) => void;
  detailedDraws: ProjectLineItemsAndSummary[];
  setDetailedDraws: React.Dispatch<React.SetStateAction<ProjectLineItemsAndSummary[]>>;
  readonly?: boolean;
  payappDrawId?: string;
  payappMergedDraw?: ProjectLineItemsAndSummary;
  hasCalledHandlePayappSave?: boolean;
  setHasCalledHandlePayappSave: (value: boolean) => void;
  handleEditDrawSubmit: (vals: CreateOrUpdateProjectDrawDTO) => Promise<void>;
  isProjectDirty: boolean;
  onOpenExistingLimitedReport?: () => void;
  onCreateNewLimitedReport?: () => void;
  onOpenExistingMagicReport?: () => void;
  onCreateNewMagicReport?: () => void;
  isMonitoringReport?: boolean;
  setIsEditMode?: React.Dispatch<React.SetStateAction<boolean>>;
  monitoringType?: string;
  limitedReportId?: string;
  magicReportId?: string;
  /** Start Next ran and no payapp has been saved since: Open Report and New Report are greyed out. */
  magicReportAwaitingPayapp?: boolean;
  scrollHeight?: string;
  highlightedLineItemIds?: string[];
  onToggleLineItemHighlight?: (lineItemId: string) => void;
}

const renderColCell = (highlightMismatch = false, drawInputRetainagePercentage?: number) => {
  // eslint-disable-next-line react/prop-types
  const CellRenderer = ({ cell, row }: { cell: MRT_Cell<ProjectLineItemDTO>; row: MRT_Row<ProjectLineItemDTO> }) => {
    const value = cell.getValue<number>();
    const numericValue = typeof value === 'number' && Number.isFinite(value) ? value : typeof value === 'string' ? parseCurrency(value) : 0;
    const isPercentageComplete = cell.column.id === 'percentageComplete';
    const isTotalRow = row.id === 'totals';
    const alwaysTwoDecimals = cell.column.id === 'reallocation';

    let textColor = 'inherit';
    let fontWeight: React.CSSProperties['fontWeight'] = '300';

    if (numericValue < 0 && !isTotalRow) {
      textColor = NEGATIVE_NUMBER_COLOR;
    }

    if (highlightMismatch) {
      const { previousApplication, completeThisPeriod, storedThisPeriod } = row.original;
      const completedStoredToDate =
        (Number(previousApplication) || 0) + (Number(completeThisPeriod) || 0) + (Number(storedThisPeriod) || 0);
      const completedStoredToDateForCDCCApproved = (Number(completeThisPeriod) || 0) + (Number(storedThisPeriod) || 0);

      if (cell.column.id === 'retainage') {
        // Use cdccApproved-based total (matches the auto-calc formula) so the check is consistent
        const totalForRetainage = calculateTotalCompletedStored(row.original);
        const expected = Math.round(totalForRetainage * (drawInputRetainagePercentage || 0));
        const valueDiffersFromExpected = numericValue !== null && numericValue !== undefined && Math.round(numericValue) !== expected;
        if (valueDiffersFromExpected || row.original.manualOverrides?.retainage) {
          fontWeight = '600';
        }
      } else if (cell.column.id === 'cdccApproved') {
        const expected = +completedStoredToDateForCDCCApproved.toFixed(2);
        if (+numericValue.toFixed(2) !== expected) {
          fontWeight = '600';
        }
      }
    }

    if (!isTotalRow && isChangedFromLastPeriod(row.original, cell.column.id)) {
      fontWeight = '600';
    }

    // Check if this column has a note
    const noteContent = row.original.note?.noteContent?.find((content: any) => content.field === cell.column.id);
    const hasNote = !!noteContent && noteContent.payappValue !== noteContent.projectValue;

    return (
      <div
        style={{
          color: textColor,
          fontWeight,
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          overflow: 'hidden',
          justifyContent: 'flex-end',
        }}
      >
        <span
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flex: hasNote ? '0 1 auto' : '1',
            minWidth: 0,
          }}
        >
          {isPercentageComplete
            ? isTotalRow
              ? ''
              : formatPercentage(value?.toString())
            : alwaysTwoDecimals
              ? formatCurrency(numericValue.toString(), false, false, true)
              : formatCurrency(Math.round(numericValue).toString(), false)}
        </span>
        {hasNote && (
          <Tooltip
            title={
              <div style={{ padding: 4 }}>
                <div>
                  <strong>PayApp:</strong>{' '}
                  {alwaysTwoDecimals
                    ? formatCurrency((Number(noteContent.payappValue) || 0).toString(), false, false, true)
                    : formatCurrency(Math.round(Number(noteContent.payappValue) || 0).toString(), false)}
                </div>
                <div>
                  <strong>{noteContent.projectValue !== undefined ? 'Last Month' : 'System'}:</strong>{' '}
                  {alwaysTwoDecimals
                    ? formatCurrency((Number(noteContent.projectValue ?? value) || 0).toString(), false, false, true)
                    : formatCurrency(Math.round(Number(noteContent.projectValue ?? value) || 0).toString(), false)}
                </div>
              </div>
            }
            arrow
          >
            <Info sx={{ fontSize: 16, color: 'rgb(241, 161, 59)', ml: 0.25, flexShrink: 0, marginLeft: 1 }} />
          </Tooltip>
        )}
      </div>
    );
  };

  CellRenderer.displayName = highlightMismatch ? 'InputRetMismatchCell' : 'DefaultNumberCell';

  return CellRenderer;
};

// New component for description cell with note
// eslint-disable-next-line react/prop-types
const DescriptionCellRenderer = ({ cell, row }: { cell: MRT_Cell<ProjectLineItemDTO>; row: MRT_Row<ProjectLineItemDTO> }) => {
  const description = cell.getValue<string>();
  const isChangeOrder = (row.original.labels || []).includes(LineItemLabelEnum.ChangeOrder);
  // Bolding-only entries (payappValue === projectValue) mark last-period changes, not payapp discrepancies.
  const flaggedNotes = row.original.note?.noteContent?.filter((content: any) => content.payappValue !== content.projectValue) ?? [];
  const hasNote = flaggedNotes.length > 0;
  const isTotalRow = row.id === 'totals';
  const isSubtotal = row.original.__rowType === 'subtotal';

  if (isTotalRow) {
    return <span>{description}</span>;
  }

  if (isSubtotal) {
    return (
      <Stack direction='row' alignItems='center' spacing={1} sx={{ width: '100%' }}>
        <Functions fontSize='small' sx={{ color: 'primary.main' }} />
        <Typography variant='body2' sx={{ fontWeight: 700 }}>
          {description}
        </Typography>
      </Stack>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        overflow: 'hidden',
      }}
    >
      {isChangeOrder && (
        <span
          style={{
            color: '#8c8c8c',
            marginRight: 4,
            whiteSpace: 'nowrap',
            flexShrink: 0,
          }}
        >
          CO:
        </span>
      )}
      <span
        style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          flex: hasNote ? '0 1 auto' : '1',
          minWidth: 0,
        }}
      >
        {description}
      </span>
      {hasNote && (
        <Tooltip
          title={
            <div style={{ textAlign: 'left', padding: '4px' }}>
              <div style={{ fontWeight: 600, marginBottom: '4px' }}>Flagged Fields on PayApp Upload:</div>
              <ul style={{ paddingLeft: '1.25rem', margin: 0 }}>
                {flaggedNotes.map((note: any, idx: number) => (
                  <li key={idx}>{friendlyFieldNames[note.field] || note.field}</li>
                ))}
              </ul>
            </div>
          }
          arrow
        >
          <Info sx={{ fontSize: 16, color: 'rgb(241, 161, 59)', ml: 0.25, flexShrink: 0 }} />
        </Tooltip>
      )}
    </div>
  );
};

// Mounted per cell in edit mode, so each edit has its own glossary search.
const DescriptionCellEditor = ({
  cell,
  table,
  onCommit,
}: {
  cell: MRT_Cell<ProjectLineItemDTO>;
  table: MRT_TableInstance<ProjectLineItemDTO>;
  onCommit: (picked: LineItemDTO) => void;
}) => {
  const { lineItemOptions, isSearching, searchLineItems, addCreatedOption } = useLineItemSearch(true);
  const [descriptionInput, setDescriptionInput] = React.useState(cell.row.original.lineItem.description);
  const [selectedOption, setSelectedOption] = React.useState<LineItemDTO | null>({
    id: cell.row.original.lineItem.id,
    description: cell.row.original.lineItem.description,
  });

  React.useEffect(() => {
    addCreatedOption({ id: cell.row.original.lineItem.id, description: cell.row.original.lineItem.description });
  }, []);

  const handleDescriptionInputChange = (value: string) => {
    setDescriptionInput(value);
    // Typing clears the previous selection.
    setSelectedOption(null);
    searchLineItems(value);
  };

  const commit = (picked: LineItemDTO) => {
    setSelectedOption(picked);
    onCommit(picked);
    table.setEditingCell(null);
  };

  const handleSaveNew = (created: LineItemDTO) => {
    addCreatedOption(created);
    commit(created);
  };

  return (
    // Column header already shows "Description".
    <Box sx={{ width: '100%', '& .MuiInputLabel-root': { display: 'none' }, '& .MuiInputBase-root': { marginTop: 0 } }}>
      <DescriptionGlossaryField
        descriptionInput={descriptionInput}
        onDescriptionInputChange={handleDescriptionInputChange}
        options={lineItemOptions}
        isSearching={isSearching}
        selectedOption={selectedOption}
        onSelectExisting={commit}
        onSaveNew={handleSaveNew}
        // Exit edit mode without saving; the row keeps its original description.
        onDismiss={() => table.setEditingCell(null)}
      />
    </Box>
  );
};

// The actions column is collapsed to a narrow strip so it stops consuming ~120px of every row.
// Each row keeps a faint hint glyph so the column is still discoverable, and hovering the row
// floats the real buttons leftwards over the adjacent cells. The column's width never changes,
// so nothing in the table reflows on hover.
const ACTIONS_COLUMN_WIDTH = 44;

// Nudge that lines the header pill up with the row pills. `right` is measured from each cell's
// padding box and the header cell's padding differs from the body's, but the two don't reconcile
// to a figure that can be derived cleanly from the padding values alone — this is measured
// against the rendered table. Positive values move the pill left. It is the one knob for this
// alignment; if the header drifts again, adjust it here.
const HEADER_PILL_RIGHT_OFFSET = 3;

const revealPillSx = (right = 0) => ({
  position: 'absolute' as const,
  right,
  top: '50%',
  transform: 'translateY(-50%)',
  display: 'flex',
  alignItems: 'center',
  px: 0.75,
  py: 0.25,
  borderRadius: 20,
  backgroundColor: 'rgba(255, 255, 255, 0.97)',
  boxShadow: '0 1px 6px rgba(0, 0, 0, 0.18)',
  zIndex: 3,
});

// eslint-disable-next-line react/prop-types
const HoverRowActions: React.FC<{ children: React.ReactNode; alwaysVisible?: boolean }> = ({ children, alwaysVisible }) => (
  <Box
    sx={{
      position: 'relative',
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      // Reveal is keyed to hovering this strip, NOT the row. The pill floats left over the last
      // data column (Retainage), which is editable — a row-wide trigger would sit on top of it and
      // block editing. The pill is a DOM child of this Box, so moving the pointer onto the buttons
      // keeps the hover alive even though they render outside the cell.
      '& .row-actions-reveal': { opacity: 0, pointerEvents: 'none', transition: 'opacity 120ms ease' },
      '&:hover .row-actions-reveal': { opacity: 1, pointerEvents: 'auto' },
      '& .row-actions-hint': { opacity: 0.45, transition: 'opacity 120ms ease' },
      '&:hover .row-actions-hint': { opacity: 0 },
    }}
  >
    {/* Persistent affordance: tells the user actions live here before they hover. */}
    {!alwaysVisible && <MoreVert className='row-actions-hint' fontSize='small' sx={{ color: 'text.disabled' }} />}
    <Box className={alwaysVisible ? undefined : 'row-actions-reveal'} sx={revealPillSx()}>
      {children}
    </Box>
  </Box>
);

function ProjectTable({
  projectId,
  projectNumber,
  projectName,
  projectDrawId,
  projectDrawName,
  drawInputRetainagePercentage,
  onDrawInputRetainagePercentageChange,
  inputRetainagePercentageHasError,
  lineItems,
  payappDraftId,
  openAutoAssignDialog,
  predictingDivisions = false,
  divisionsData,
  isLoading,
  updateProject,
  detailedDraws,
  setDetailedDraws,
  readonly,
  payappDrawId,
  payappMergedDraw,
  hasCalledHandlePayappSave,
  setHasCalledHandlePayappSave,
  handleEditDrawSubmit,
  isProjectDirty,
  onOpenExistingLimitedReport,
  onCreateNewLimitedReport,
  onOpenExistingMagicReport,
  onCreateNewMagicReport,
  isMonitoringReport = false,
  setIsEditMode,
  monitoringType,
  limitedReportId,
  magicReportId,
  magicReportAwaitingPayapp = false,
  scrollHeight,
  highlightedLineItemIds,
  onToggleLineItemHighlight,
}: ProjectTableProps) {
  const [isFullScreen, setIsFullScreen] = React.useState(false);
  const [isSearchOpen, setIsSearchOpen] = React.useState(false);
  // Retainage threshold stored as display percentage (e.g. 10 for 10%). Decimal = value / 100.
  const [retainagePercentage, setRetainagePercentage] = React.useState<number>((drawInputRetainagePercentage ?? 0.1) * 100);
  // Local draft for the input retainage percentage field so keystrokes don't push into shared project state until blur.
  const [inputRetainagePercentageDraft, setInputRetainagePercentageDraft] = React.useState<number | string>(
    Math.round((drawInputRetainagePercentage ?? 0.1) * 100 * 100) / 100,
  );
  // Tracks the last retainage % (display) we already processed, so post-save prop resets don't re-trigger recalculation.
  const processedRetainageRef = React.useRef((drawInputRetainagePercentage ?? 0.1) * 100);
  // Ref mirror of isProjectDirty so the retainage effect can read it without adding it to deps.
  const isProjectDirtyRef = React.useRef(isProjectDirty);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const cacheKey = `lineItems-table-preferences`;
  const [selectedLineItem, setSelectedLineItem] = React.useState<ProjectLineItemDTO | null>(null);
  const [editDrawLineItemDialogOpen, setEditDrawLineItemDialogOpen] = React.useState(false);
  const [selectedLineItemsIds, setSelectedLineItemsIds] = React.useState<string[]>([]);
  const [bulkDeleteInProgress, setBulkDeleteInProgress] = React.useState(false);
  const [bulkEditDialogOpen, setBulkEditDialogOpen] = React.useState(false);
  const [bulkEditInProgress, setBulkEditInProgress] = React.useState(false);
  const [addingLineItem, setAddingLineItem] = React.useState(false);
  const [scrollToRowId, setScrollToRowId] = React.useState<string | null>(null);
  const [addChangeOrdersDialogOpen, setAddChangeOrdersDialogOpen] = React.useState(false);
  const [addingChangeOrders, setAddingChangeOrders] = React.useState(false);
  const [payAppUploadDialogOpen, setPayAppUploadDialogOpen] = React.useState(false);
  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState<MRT_VisibilityState>(
    isMonitoringReport ? { division: false } : {},
  );
  const [rowSelection, setRowSelection] = React.useState<MRT_RowSelectionState>({});
  const [sorting, setSorting] = React.useState<MRT_SortingState>([]);
  const showPayappControls = process.env.REACT_APP_SHOW_PAYAPP_CONTROLS == 'true';
  const showMagicReport = process.env.REACT_APP_SHOW_MAGIC_REPORT == 'true';
  const { getConfirmation } = usePrompt();

  const currentDrawSubtotals = React.useMemo(
    () => detailedDraws.find(draw => draw.projectDrawSummary.id === projectDrawId)?.subtotals ?? [],
    [detailedDraws, projectDrawId],
  );

  const handleSubtotalsChange = React.useCallback(
    (nextSubtotals: ProjectLineItemSubtotalDTO[]) => {
      const updatedDetailedDraws = detailedDraws.map(draw =>
        draw.projectDrawSummary.id === projectDrawId ? { ...draw, subtotals: nextSubtotals } : draw,
      );
      setDetailedDraws(updatedDetailedDraws);
      updateProject({ projectDraws: updatedDetailedDraws }, true);
    },
    [detailedDraws, projectDrawId, setDetailedDraws, updateProject],
  );

  const {
    subtotalDialogState,
    subtotalDialogSaving,
    handleInsertSubtotalClick,
    handleDeleteSubtotalClick,
    handleSubtotalDialogClose,
    handleSubtotalDialogSubmit,
  } = useSubtotalActions({
    drawId: projectDrawId,
    subtotals: currentDrawSubtotals,
    onSubtotalsChange: handleSubtotalsChange,
    getConfirmation,
  });

  const handleRangeEditApply = React.useCallback(
    (result: SubtotalRangeApplyResult) => {
      const isPersisted = !result.subtotalId.startsWith('temp-subtotal-');
      const updatedDetailedDraws = detailedDraws.map(draw => {
        if (draw.projectDrawSummary.id !== projectDrawId) return draw;
        const nextSubtotals = (draw.subtotals ?? []).map(s =>
          s.id === result.subtotalId
            ? {
                ...s,
                anchorDrawLineItemId: result.anchorDrawLineItemId,
                anchorSerialNumber: result.anchorSerialNumber,
                range: result.range,
              }
            : s,
        );
        return {
          ...draw,
          lineItems: result.reorderedLineItems,
          subtotals: nextSubtotals,
          // Already-persisted subtotals get their anchor/range change flushed on the next Save,
          // via the same pendingSubtotalReanchors mechanism used when a line item anchoring a
          // subtotal is deleted — NOT sent immediately. It's coupled to the line item reorder
          // above, which itself only persists on Save (isEdited-flagged), so an immediate call
          // here would let the two halves of this change fall out of sync if the user cancels or
          // navigates away before saving. Not-yet-saved subtotals (temp-subtotal- ids) don't need
          // this at all — their anchor/range is read fresh from `subtotals` at save time by
          // ProjectWrapper.tsx's createSubtotals mapping.
          pendingSubtotalReanchors: isPersisted
            ? [
                ...(draw.pendingSubtotalReanchors ?? []).filter(r => r.subtotalId !== result.subtotalId),
                { subtotalId: result.subtotalId, newAnchorDrawLineItemId: result.anchorDrawLineItemId, range: result.range },
              ]
            : draw.pendingSubtotalReanchors,
        };
      });
      setDetailedDraws(updatedDetailedDraws);
      updateProject({ projectDraws: updatedDetailedDraws }, true);
    },
    [detailedDraws, projectDrawId, setDetailedDraws, updateProject],
  );

  const rangeEditor = useSubtotalRangeEditor({
    lineItems,
    subtotals: currentDrawSubtotals,
    onApply: handleRangeEditApply,
  });

  // While a subtotal is in range-edit mode, the grid's checkbox column is repurposed to show/
  // toggle that subtotal's claimed line items instead of the normal bulk-selection state.
  const rangeEditSelectionState = React.useMemo(() => {
    const state: MRT_RowSelectionState = {};
    rangeEditor.checkedIds.forEach(id => {
      state[id] = true;
    });
    return state;
  }, [rangeEditor.checkedIds]);

  const handleRowSelectionChange = React.useCallback(
    (updaterOrValue: MRT_RowSelectionState | ((old: MRT_RowSelectionState) => MRT_RowSelectionState)) => {
      if (rangeEditor.rangeEditSubtotalId) {
        const next = typeof updaterOrValue === 'function' ? updaterOrValue(rangeEditSelectionState) : updaterOrValue;
        const prevKeys = new Set(Object.keys(rangeEditSelectionState).filter(k => rangeEditSelectionState[k]));
        const nextKeys = new Set(Object.keys(next).filter(k => next[k]));
        for (const id of Array.from(nextKeys)) {
          if (!prevKeys.has(id)) {
            rangeEditor.toggleItem(id, true);
            return;
          }
        }
        for (const id of Array.from(prevKeys)) {
          if (!nextKeys.has(id)) {
            rangeEditor.toggleItem(id, false);
            return;
          }
        }
        return;
      }
      setRowSelection(updaterOrValue);
    },
    [rangeEditor, rangeEditSelectionState],
  );

  const divisions = divisionsData || [];
  const { commonMRTStyling, getRowStyling, getActionsColumnStyling, getOnSortingChangeWithToast } = useMRTStyling<any>();
  const tableRef = React.useRef<MRT_TableInstance<ProjectLineItemDTO> | null>(null);

  const [hasCalledHandleEditDrawSubmit, setHasCalledHandleEditDrawSubmit] = React.useState(false);

  const [undoHistory, setUndoHistory] = React.useState<ProjectLineItemDTO[]>([]);
  const pristineLineItemsRef = React.useRef<Record<string, ProjectLineItemDTO>>({});

  React.useEffect(() => {
    setUndoHistory([]);
    pristineLineItemsRef.current = {};
  }, [projectDrawId]);

  const openPayAppUploadDialog = () => {
    if (isProjectDirty) {
      toast.error('You have unsaved changes. Please save before uploading a new payapp.');
      return;
    }

    setPayAppUploadDialogOpen(true);
  };
  const closePayAppUploadDialog = () => setPayAppUploadDialogOpen(false);

  const openPayAppDraft = async () => {
    if (isProjectDirty) {
      toast.error('You have unsaved changes. Please save before opening a payapp draft.');
      return;
    }

    if (!payappDraftId) {
      toast.error('No draft ID available.');
      return;
    }

    try {
      const draftData = await getPayappDraft(projectDrawId);

      // Extract activeStep from the data object
      const activeStep = draftData.activeStep || 0;
      const stepId = payappStepIds[activeStep] || payappStepIds[0];

      // Convert backend field names back to display labels for coverFields
      const displayCoverFields: Record<string, string> = {};
      Object.entries(draftData.step1?.coverPageValues || {}).forEach(([backendField, value]) => {
        const displayLabel = backendToLabelMap[backendField];
        if (displayLabel) {
          displayCoverFields[displayLabel] = value as string;
        }
      });

      // Get file URL from blobName if available
      let fileUrl: string | null = null;
      if (draftData.fileBlobName) {
        try {
          fileUrl = await getPayappFileUrl(draftData.fileBlobName);
        } catch (error) {
          console.error('Error fetching file URL:', error);
          toast.error('Failed to load PDF file');
        }
      }

      // Navigate to process-payapp with the draft data in the correct format
      navigate(`${ROUTES.PROJECTS}/${projectId}${ROUTES.PROCESSPAYAPP}/${projectDrawId}/${stepId}`, {
        state: {
          // Format data to match what PayappProcess expects
          parsedTable: draftData.step2?.tableRows || [],
          coverFields: displayCoverFields,
          projectId,
          projectNumber,
          projectName,
          payappDrawId: projectDrawId,
          projectDraw: draftData.step3?.projectDraw || null,
          divisions: divisionsData,
          fileUrl: fileUrl,
          sourceTargetHeadersMapping: draftData.step2?.fieldHeadersMapping || {},

          // Additional draft-specific data
          isDraft: true,
          draftActiveStep: activeStep,
          draftFieldHeadersMapping: draftData.step2?.fieldHeadersMapping || {},
          draftSelectedRowIds: draftData.step2?.selectedRowIds || [],
          draftNotes: draftData.step3?.notes || [],
          draftOriginalCoverPageValues: draftData.step1?.originalCoverPageValues || null,
          draftOriginalFieldHeadersMapping: draftData.step2?.originalFieldHeadersMapping || null,
          draftAiFixedHeaderColumns: draftData.step2?.aiFixedHeaderColumns || [],
          draftEditedCellKeys: draftData.step2?.editedCellKeys || [],
          draftOcrFixedCellKeys: draftData.step2?.ocrFixedCellKeys || [],
          draftMergeMetrics: draftData.step3?.mergeMetrics || null,
        },
      });
    } catch (error) {
      toast.error('Failed to load PayApp draft.');
      console.error('Error loading PayApp draft:', error);
    }
  };

  // Keep isProjectDirtyRef in sync so the retainage effect below can read it without adding
  // isProjectDirty to that effect's dep array. Must be declared before the retainage effect
  // so React runs it first when both fire in the same render.
  React.useEffect(() => {
    isProjectDirtyRef.current = isProjectDirty;
  }, [isProjectDirty]);

  // When switching draws, sync state and processedRetainageRef so the retainage effect below
  // sees the new value as already-processed and skips recalculation.
  React.useEffect(() => {
    const newDisplay = (drawInputRetainagePercentage ?? 0.1) * 100;
    processedRetainageRef.current = newDisplay;
    setRetainagePercentage(newDisplay);
    setInputRetainagePercentageDraft(Math.round(newDisplay * 100) / 100);
  }, [projectDrawId]);

  // When Budget Information retainage % changes, recalculate non-manual line items.
  // Skip if: value is already processed (covers draw-switch case and floating-point imprecision),
  // or the project is clean (post-save/cancel prop refresh — not a user-initiated change).
  React.useEffect(() => {
    const newDisplay = (drawInputRetainagePercentage ?? 0.1) * 100;
    // Tolerance handles floating-point imprecision from the /100 → *100 round-trip in the parent.
    if (Math.abs(newDisplay - processedRetainageRef.current) < 0.001) {
      processedRetainageRef.current = newDisplay;
      return;
    }
    // If the project is not dirty the prop change is a post-save/cancel reset — sync state but skip recalculation.
    if (!isProjectDirtyRef.current) {
      processedRetainageRef.current = newDisplay;
      setRetainagePercentage(newDisplay);
      setInputRetainagePercentageDraft(Math.round(newDisplay * 100) / 100);
      return;
    }
    processedRetainageRef.current = newDisplay;
    setInputRetainagePercentageDraft(Math.round(newDisplay * 100) / 100);
    handleRetainagePercentageChange(newDisplay);
  }, [drawInputRetainagePercentage]);

  React.useEffect(() => {
    const ids = Object.keys(rowSelection);
    setSelectedLineItemsIds(ids);
  }, [rowSelection]);

  const handleRowClick = (row: MRT_Row<ProjectLineItemDTO>) => {
    setSelectedLineItem(row.original as ProjectLineItemDTO);
  };

  const recordUndoSnapshot = (oldRow: ProjectLineItemDTO) => {
    if (oldRow.id && !pristineLineItemsRef.current[oldRow.id] && !oldRow.isEdited) {
      pristineLineItemsRef.current[oldRow.id] = oldRow;
    }
    setUndoHistory(prev => [...prev, oldRow].slice(-MAX_UNDO_HISTORY));
  };

  const processRowUpdate = (updatedRow: any, oldRow: any) => {
    const coerced = { ...updatedRow };
    numericColumns.forEach(field => {
      coerced[field] = parseCurrency(updatedRow[field] ?? 0);
    });
    if (compareArrays(coerced, oldRow)) return oldRow;
    recordUndoSnapshot(oldRow);
    handleEditDrawLineItem(coerced);
    return coerced;
  };

  const handleUndo = () => {
    if (undoHistory.length === 0) return;
    const previousLineItem = undoHistory[undoHistory.length - 1];
    const pristine = previousLineItem.id ? pristineLineItemsRef.current[previousLineItem.id] : undefined;

    let isBackToPristine = false;
    if (pristine && !pristine.isEdited) {
      const stripFlags = (item: ProjectLineItemDTO) => {
        const copy: any = { ...item };
        EDIT_FLAG_FIELDS.forEach(field => delete copy[field]);
        return copy;
      };
      isBackToPristine = compareArrays(stripFlags(previousLineItem), stripFlags(pristine));
      if (isBackToPristine) {
        delete pristineLineItemsRef.current[previousLineItem.id as string];
      }
    }

    handleEditDrawLineItem(previousLineItem, { forceNotEdited: isBackToPristine });
    setUndoHistory(prev => prev.slice(0, -1));
  };

  React.useEffect(() => {
    if (payappMergedDraw && payappMergedDraw.lineItems && payappMergedDraw.lineItems.length > 0 && !hasCalledHandlePayappSave) {
      // Only the payapp draw's own table may perform the merge. The tab can briefly be mounted for a
      // different draw while the draw list re-sorts, and merging from there would source the budget
      // summary (and its identity) from the wrong draw.
      if (projectDrawId !== payappDrawId) return;
      const currentDraw = detailedDraws.find(draw => draw.projectDrawSummary.id === payappDrawId);
      // Wait until the draw's budget summary has been loaded from the API before computing
      // previous funding amounts. getProjectDraws returns minimal data (no budgetSummary), and
      // getBudgetandLineItems populates it asynchronously. Running before it's ready would give
      // requestedAmount = 0 and skip the funding row entirely.
      if (!currentDraw || !currentDraw.budgetSummary) return;

      // Capture the current requestedAmount BEFORE updating with payapp data
      const currentBudgetSummary = currentDraw.budgetSummary;
      const currentRequestedAmount = currentBudgetSummary?.requestedAmount || 0;
      const currentApprovedAmount = currentBudgetSummary?.totalAmountReccForFunding || 0;
      const currentRetainage = currentBudgetSummary?.totalRetainageHeldPercentage || 0;
      const currentPercentComplete = currentBudgetSummary?.totalContractBudgetPerComplete || 0;
      const currentTotalContractAmount = currentBudgetSummary?.contractBudgetSumToDate || 0;
      const currentPercentRequested = currentTotalContractAmount > 0 ? (currentRequestedAmount / currentTotalContractAmount) * 100 : 0;

      // Build previous fundings: existing base → normal current row → skipped-payapp rows.
      // Order matters: the normal row is numbered sequentially from the existing base,
      // then any skipped rows (e.g. "2*") are appended after so the table reads in order.
      const baseFundings = currentDraw.projectDrawSummary.previousFundings || [];
      let updatedPreviousFundings = baseFundings;

      if (currentRequestedAmount > 0) {
        const existingFundingsCount = baseFundings.length;
        // Number the current row sequentially from the original base (before any skipped rows),
        // so getNextApplicationNumber sees e.g. [] → "1", not ["2*"] → "3".
        const nextApplicationNumber = getNextApplicationNumber(baseFundings);

        // The Step 4 "Less Previous Fundings" box defaults to this same system value
        // (lessPreviousFundings + Recommended Amount — see computeOriginalLPF in
        // PayappProcessFooter) but is user-editable. If the user edited it, that edited
        // value — not the system-computed amount — is what actually got funded/received,
        // so it drives this row instead.
        const systemLessPreviousFunding = (currentBudgetSummary?.lessPreviousFundings || 0) + currentApprovedAmount;
        const rawUpdatedLessPreviousFunding = (payappMergedDraw.budgetSummary as any)?.updatedLessPreviousFunding;
        const fundedAmount =
          rawUpdatedLessPreviousFunding !== undefined
            ? parseCurrency(rawUpdatedLessPreviousFunding.toString()) - systemLessPreviousFunding + currentApprovedAmount
            : currentApprovedAmount;

        const newPreviousFunding = {
          id: `temp-${Date.now()}`,
          applicationNumber: nextApplicationNumber,
          applicationDate: currentDraw.projectDrawSummary.periodEnding || undefined,
          amountFunded: fundedAmount,
          receivedByContractor: fundedAmount,
          approvedAmount: fundedAmount,
          retainage: currentRetainage,
          percentRequested: currentPercentRequested,
          percentComplete: currentPercentComplete,
          totalContractAmount: currentTotalContractAmount,
          displayOrder: existingFundingsCount + 1,
          comment: undefined,
          reviewedByCDCC: true,
        };
        updatedPreviousFundings = [...updatedPreviousFundings, newPreviousFunding];
      }

      // Append skipped-payapp rows AFTER the normal current row so the table reads in order:
      // existing → "1" (current) → "2*" (skipped). Identify extra rows by IDs not in the base.
      const baseFundingIds = new Set(baseFundings.map((f: any) => f.id));
      const additionalFundings = (payappMergedDraw.projectDrawSummary?.previousFundings ?? []).filter(
        (f: any) => !baseFundingIds.has(f.id),
      );
      if (additionalFundings.length > 0) {
        updatedPreviousFundings = [...updatedPreviousFundings, ...additionalFundings];
      }

      // Mark the draw as edited and trigger edit mode
      setIsEditMode && setIsEditMode(true);

      // Create an updated draw with proper edit flags, including updated previousFundings
      const updatedDraw = {
        ...payappMergedDraw,
        isEdited: true,
        projectDrawSummary: {
          ...currentDraw.projectDrawSummary,
          // Pin the identity to the payapp draw so the merged result can never be written under
          // another draw's id/name.
          id: payappDrawId,
          name: currentDraw.projectDrawSummary.name,
          applicationNumber: payappMergedDraw.projectDrawSummary?.applicationNumber,
          periodBeginning: payappMergedDraw.projectDrawSummary?.periodBeginning || currentDraw.projectDrawSummary?.periodBeginning,
          periodEnding: payappMergedDraw.projectDrawSummary?.periodEnding || currentDraw.projectDrawSummary?.periodEnding,
          inputRetainagePercentage:
            payappMergedDraw.projectDrawSummary?.inputRetainagePercentage ?? currentDraw.projectDrawSummary?.inputRetainagePercentage,
          previousFundings: updatedPreviousFundings, // Include the updated previous fundings
          isEnabled: true,
          isEdited: true,
        },
        budgetSummary: payappMergedDraw.budgetSummary
          ? {
              ...payappMergedDraw.budgetSummary,
              isEdited: true,
            }
          : undefined,
      };

      const updatedDetailedDraws = detailedDraws.map(draw => {
        if (draw.projectDrawSummary.id === payappDrawId) return updatedDraw;
        else return draw;
      });

      setDetailedDraws(updatedDetailedDraws);
      updateProject({ projectDraws: updatedDetailedDraws, notes: payappMergedDraw.notes }, true);
      setHasCalledHandlePayappSave(true);
    }
  }, [detailedDraws, payappMergedDraw, lineItems, projectDrawId, setIsEditMode, payappDrawId, projectDrawId]);

  const handleRetainagePercentageChange = (newPercentageDisplay: number) => {
    const newDecimal = newPercentageDisplay / 100;
    processedRetainageRef.current = newPercentageDisplay;
    setRetainagePercentage(newPercentageDisplay);

    const updatedDetailedDraws = detailedDraws.map(draw => {
      if (draw.projectDrawSummary.id !== projectDrawId) return draw;
      return {
        ...draw,
        isEdited: true,
        projectDrawSummary: {
          ...draw.projectDrawSummary,
          inputRetainagePercentage: newPercentageDisplay,
          isEdited: true,
        },
        lineItems: draw.lineItems?.map(item => {
          const completedStoredToDate =
            (item.previousApplication || 0) + (typeof item.cdccApproved === 'number' ? item.cdccApproved : calculateCdccApproved(item));
          // An override that matches the new % is no longer an override.
          if (item.manualOverrides?.retainage && (item.retainage || 0) === Math.round(completedStoredToDate * newDecimal)) {
            return { ...item, manualOverrides: { ...item.manualOverrides, retainage: false }, isEdited: !item.isCreated };
          }
          const oldExpected = Math.round(completedStoredToDate * (retainagePercentage / 100));
          // Skip items whose value doesn't match the old expected — they're user-overridden (bold).
          if (Math.round(item.retainage || 0) !== oldExpected) return item;
          return {
            ...item,
            retainage: Math.round(completedStoredToDate * newDecimal),
            isEdited: !item.isCreated,
          };
        }),
      };
    });

    setDetailedDraws(updatedDetailedDraws);
    updateProject({ projectDraws: updatedDetailedDraws }, true);
  };

  const handleAutoAssignDivisions = () => {
    openAutoAssignDialog
      ? openAutoAssignDialog(
          lineItems.map(lineItem => {
            const revisedBudget = (lineItem.originalBudget || 0) + (lineItem.reallocation || 0) + (lineItem.changeOrder || 0);
            return {
              id: lineItem.id,
              description: lineItem.lineItem.description,
              division: lineItem.lineItem.division?.name,
              revisedBudget: revisedBudget,
              isDeleted: false, // Ensure isDeleted is false when isEdited is being set
              isCreated: lineItem?.isCreated ? true : false, // Preserve isCreated if it was already true
              isEdited: !lineItem?.isCreated,
            };
          }),
        )
      : toast.error('Auto-Assign feature not available');
  };

  const handleCreateLineItemClick = () => {
    setEditDrawLineItemDialogOpen(true);
    setSelectedLineItem(null);
  };

  const currentDraw = detailedDraws.find(draw => draw.projectDrawSummary.id === projectDrawId);

  // "CO n+1" names for the next `count` change-order rows, previewed live in the dialog.
  // n is the draw's application number, so the COs line up with the most recent payapp.
  const previewChangeOrderDescriptions = React.useCallback(
    (count: number) =>
      getNextChangeOrderDescriptions(
        currentDraw?.projectDrawSummary?.applicationNumber,
        (currentDraw?.lineItems ?? []).map(item => item.lineItem?.description ?? ''),
        count,
      ),
    [currentDraw],
  );

  // Adds `count` blank rows pre-labelled as change orders. They're ordinary created line items from
  // here on — the user types budget/amount values straight into the table like any other row.
  const handleAddChangeOrders = (count: number) => {
    if (!count || count < 1) return;
    setAddingChangeOrders(true);
    try {
      const descriptions = previewChangeOrderDescriptions(count);
      const createdAt = Date.now();
      const nextSerialNumber = getNextSerialNumber(lineItems);
      const newLineItems: ProjectLineItemDTO[] = descriptions.map((description, index) => ({
        id: `temp-${createdAt}-${index}`,
        originalBudget: 0,
        changeOrder: 0,
        reallocation: 0,
        previousApplication: 0,
        completeThisPeriod: 0,
        storedThisPeriod: 0,
        cdccApproved: 0,
        quantity: 0,
        serialNumber: nextSerialNumber + index,
        revisedBudget: 0,
        completedStoredToDate: 0,
        percentageComplete: 0,
        balanceToComplete: 0,
        retainage: 0,
        labels: [LineItemLabelEnum.ChangeOrder],
        lineItem: {
          id: '',
          description,
          unit: 'unit',
          division: undefined,
        },
        projectDrawId: '',
        projectDrawName: projectDrawName,
        isIncluded: false,
        isCreated: true,
        isEdited: false,
        isDeleted: false,
      }));

      const updatedDetailedDraws = detailedDraws.map(draw => {
        if (draw.projectDrawSummary.id === projectDrawId) {
          return {
            ...draw,
            lineItems: draw.lineItems ? [...draw.lineItems, ...newLineItems] : newLineItems,
          };
        }
        return draw;
      });

      setDetailedDraws(updatedDetailedDraws);
      updateProject({ projectDraws: updatedDetailedDraws }, true);
      setScrollToRowId(newLineItems[0]?.id ?? null);
      toast.success(`Added ${newLineItems.length} change order ${newLineItems.length === 1 ? 'row' : 'rows'}`);
      setAddChangeOrdersDialogOpen(false);
    } catch (error) {
      handleError(error);
    } finally {
      setAddingChangeOrders(false);
    }
  };

  const handleUpdateLineItemClick = (row: any) => {
    setSelectedLineItem(row);
    setEditDrawLineItemDialogOpen(true);
  };

  const handleEditDrawLineItem = async (updatedLineItem: ProjectLineItemDTO, options?: { forceNotEdited?: boolean }) => {
    try {
      const isEdited = options?.forceNotEdited ? false : !updatedLineItem?.isCreated;
      // Ensure all necessary fields are populated with default values if missing
      const sanitizedLineItem: ProjectLineItemDTO = {
        ...updatedLineItem,
        originalBudget: Number(updatedLineItem.originalBudget) || 0,
        reallocation: Number(updatedLineItem.reallocation) || 0,
        changeOrder: Number(updatedLineItem.changeOrder) || 0,
        previousApplication: Number(updatedLineItem.previousApplication) || 0,
        completeThisPeriod: Number(updatedLineItem.completeThisPeriod) || 0,
        storedThisPeriod: Number(updatedLineItem.storedThisPeriod) || 0,
        isDeleted: false,
        isCreated: updatedLineItem?.isCreated ? true : false,
        isEdited,
        labels: updatedLineItem?.labels ?? [],
      };

      // Prepare the updated projectDraws
      const updatedProjectDraws = detailedDraws.map(draw => {
        if (draw.projectDrawSummary.id === projectDrawId) {
          return {
            ...draw,
            lineItems: draw.lineItems?.map(item => {
              if (item.id === updatedLineItem.id) {
                return {
                  ...sanitizedLineItem,
                  isDeleted: false,
                  isCreated: updatedLineItem?.isCreated ? true : false,
                  isEdited,
                };
              }
              return item; // Return the unchanged item
            }),
          };
        }
        return draw; // Return the unchanged draw
      });
      // Update local state
      setDetailedDraws(updatedProjectDraws);

      // Call updateProject with the new projectDraws
      updateProject(
        {
          projectDraws: updatedProjectDraws,
        },
        true,
      );
    } catch (error) {
      handleError(error);
    }
  };

  const handleCreateOrEditLineItem = async (lineItem: CreateDrawLineItemDTO) => {
    try {
      if (selectedLineItem) {
        // Update existing line item
        const updatedLineItems = detailedDraws.map(draw => {
          if (draw.projectDrawSummary.id === projectDrawId) {
            return {
              ...draw,
              lineItems: draw.lineItems?.map(item => {
                if (item.id === selectedLineItem.id) {
                  // Check if completeThisPeriod or storedThisPeriod changed
                  const oldCompleteThisPeriod = item.completeThisPeriod || 0;
                  const oldStoredThisPeriod = item.storedThisPeriod || 0;
                  const newCompleteThisPeriod = lineItem.completeThisPeriod ?? oldCompleteThisPeriod;
                  const newStoredThisPeriod = lineItem.storedThisPeriod ?? oldStoredThisPeriod;

                  const dependentFieldsChanged =
                    oldCompleteThisPeriod !== newCompleteThisPeriod || oldStoredThisPeriod !== newStoredThisPeriod;

                  // Calculate old expected value
                  const oldExpectedCdccApproved = oldCompleteThisPeriod + oldStoredThisPeriod;
                  const currentCdccApproved = item.cdccApproved ?? 0;

                  // Check if cdccApproved was manually overridden
                  const isManuallyOverridden = Math.abs(currentCdccApproved - oldExpectedCdccApproved) > 0.01;

                  // If dependent fields changed and not manually overridden, recalculate cdccApproved
                  // Keep nested lineItem in sync; save paths read description/unit/division from it.
                  const updatedLineItem = {
                    ...item,
                    ...lineItem,
                    lineItem: {
                      ...item.lineItem,
                      description: lineItem.description ?? item.lineItem.description,
                      unit: lineItem.unit ?? item.lineItem.unit,
                      division: lineItem.divisionId ? divisionsData.find(division => division.id === lineItem.divisionId) : undefined,
                    },
                    isDeleted: false,
                    isCreated: item?.isCreated ? true : false,
                    isEdited: !item?.isCreated,
                  };

                  if (dependentFieldsChanged && !isManuallyOverridden) {
                    updatedLineItem.cdccApproved = newCompleteThisPeriod + newStoredThisPeriod;
                    // Auto-update retainage when cdccApproved changes (unless user manually set retainage)
                    if (!item.manualOverrides?.retainage) {
                      const newCompletedStoredToDate = (item.previousApplication || 0) + updatedLineItem.cdccApproved;
                      updatedLineItem.retainage = Math.round(newCompletedStoredToDate * (retainagePercentage / 100));
                    }
                  }

                  // Recalculate retainage when previousApplication changes
                  const oldPreviousApplication = item.previousApplication || 0;
                  const newPreviousApplication = lineItem.previousApplication ?? oldPreviousApplication;
                  if (newPreviousApplication !== oldPreviousApplication && !item.manualOverrides?.retainage) {
                    const currentCdcc =
                      typeof updatedLineItem.cdccApproved === 'number'
                        ? updatedLineItem.cdccApproved
                        : calculateCdccApproved(updatedLineItem);
                    updatedLineItem.retainage = Math.round((newPreviousApplication + currentCdcc) * (retainagePercentage / 100));
                  }

                  recordUndoSnapshot(item);
                  return updatedLineItem;
                }
                return item;
              }),
            };
          }
          return draw;
        });

        // Update local state
        setDetailedDraws(updatedLineItems);

        // Update global state
        updateProject(
          {
            projectDraws: updatedLineItems,
          },
          true,
        );
      } else {
        // Create a new line item
        const newCdccApproved = (lineItem.completeThisPeriod || 0) + (lineItem.storedThisPeriod || 0);
        const newPreviousApplication = lineItem.previousApplication || 0;
        const autoRetainage = Math.round((newPreviousApplication + newCdccApproved) * (retainagePercentage / 100));
        const newLineItem: ProjectLineItemDTO = {
          id: `temp-${Date.now()}`, // Generate a temporary ID
          originalBudget: lineItem.originalBudget || 0,
          changeOrder: lineItem.changeOrder || 0,
          reallocation: lineItem.reallocation || 0,
          previousApplication: newPreviousApplication,
          completeThisPeriod: lineItem.completeThisPeriod || 0,
          storedThisPeriod: lineItem.storedThisPeriod || 0,
          cdccApproved: newCdccApproved,
          quantity: lineItem.quantity || 0,
          serialNumber: getNextSerialNumber(lineItems),
          revisedBudget: 0,
          completedStoredToDate: 0,
          percentageComplete: 0,
          balanceToComplete: 0,
          retainage: lineItem.retainage != null && lineItem.retainage !== 0 ? lineItem.retainage : autoRetainage,
          labels: (lineItem as CreateDrawLineItemDTO & { labels?: LineItemLabel[] }).labels ?? [],
          lineItem: {
            id: '',
            description: lineItem.description || '',
            unit: lineItem.unit || 'unit',
            division: divisionsData.find(division => division.id === lineItem.divisionId) || undefined,
          },
          projectDrawId: '',
          projectDrawName: projectDrawName,
          isIncluded: false,
          isCreated: true,
          isEdited: false,
          isDeleted: false,
        };

        const updatedLineItems = detailedDraws.map(draw => {
          if (draw.projectDrawSummary.id === projectDrawId) {
            return {
              ...draw,
              lineItems: draw.lineItems ? [...draw.lineItems, newLineItem] : [newLineItem],
            };
          }
          return draw;
        });

        setDetailedDraws(updatedLineItems);

        updateProject(
          {
            projectDraws: updatedLineItems,
          },
          true,
        );
        setScrollToRowId(newLineItem.id);
      }
    } catch (error) {
      handleError(error);
    } finally {
      setAddingLineItem(false);
      setEditDrawLineItemDialogOpen(false);
    }
  };

  const handleBulkEditLineItems = async (values: {
    divisionId: string | null;
    unit: LineItemUnit;
    labels?: LineItemLabel[];
    shouldUpdateLabels?: boolean;
  }) => {
    if (selectedLineItemsIds.length === 0) {
      toast.error('No Line Items to edit. Please select one or more rows.');
      return;
    }
    setBulkEditInProgress(true);
    try {
      const updatedDetailedDraws: ProjectLineItemsAndSummary[] = detailedDraws.map(draw => {
        if (draw.projectDrawSummary.id === projectDrawId) {
          return {
            ...draw,
            lineItems: draw.lineItems?.map(lineItem => {
              if (selectedLineItemsIds.includes(lineItem.id)) {
                recordUndoSnapshot(lineItem);
                return {
                  ...lineItem,
                  lineItem: {
                    ...lineItem.lineItem,
                    ...(values.divisionId ? { division: divisionsData.find(division => division.id === values.divisionId) } : {}),
                    ...(values.unit ? { unit: values.unit } : {}),
                  },
                  isDeleted: false, // Ensure isDeleted is false when isEdited is being set
                  isCreated: lineItem?.isCreated ? true : false, // Preserve isCreated if it was already true
                  isEdited: !lineItem?.isCreated,
                  ...(values.shouldUpdateLabels
                    ? (() => {
                        const currentLabels = lineItem.labels ?? [];
                        const desiredHasCO = (values.labels ?? []).includes(LineItemLabelEnum.ChangeOrder);
                        const labelSet = new Set(currentLabels);
                        if (desiredHasCO) {
                          labelSet.add(LineItemLabelEnum.ChangeOrder);
                        } else {
                          labelSet.delete(LineItemLabelEnum.ChangeOrder);
                        }
                        return { labels: Array.from(labelSet) };
                      })()
                    : {}),
                };
              }
              return lineItem;
            }) as ProjectLineItemDTO[],
          };
        }
        return draw;
      });

      setDetailedDraws(updatedDetailedDraws);

      updateProject(
        {
          projectDraws: updatedDetailedDraws,
        },
        true,
      );
      setBulkEditDialogOpen(false);
    } catch (error) {
      handleError(error);
    } finally {
      setBulkEditInProgress(false);
    }
  };

  const handleDeleteLineItemClick = async (row: ProjectLineItemDTO) => {
    if (!row.id) {
      toast.error('No Line Item to delete. Please try again.');
      return;
    }

    try {
      const isConfirmed = await getConfirmation({
        heading: `Delete ${row.lineItem.description}`,
        description: 'Are you sure you want to delete this line item?',
        confirmButton: {
          label: 'Delete',
          color: 'error',
        },
        cancelButton: {
          label: 'Cancel',
          color: 'secondary',
        },
      });
      if (!isConfirmed) return;

      // Update local state
      const updatedDetailedDraws = detailedDraws.map(draw => {
        if (draw.projectDrawSummary.id === projectDrawId) {
          const { updatedSubtotals, reanchors } = computeSubtotalReanchors(new Set([row.id]), draw.subtotals ?? [], draw.lineItems ?? []);
          return {
            ...draw,
            lineItems: draw.lineItems?.map(item => {
              if (item.id === row.id) {
                return {
                  ...item,
                  isDeleted: true, // Set the isDeleted flag to true
                  isEdited: false,
                  isCreated: false,
                };
              }
              return item; // Return unmodified item
            }),
            subtotals: updatedSubtotals,
            pendingSubtotalReanchors:
              reanchors.length > 0 ? [...(draw.pendingSubtotalReanchors ?? []), ...reanchors] : draw.pendingSubtotalReanchors,
          };
        }
        return draw; // Return unmodified draw
      });

      setDetailedDraws(updatedDetailedDraws);

      // Update global state
      updateProject(
        {
          projectDraws: updatedDetailedDraws,
        },
        true,
      );
    } catch (error) {
      handleError(error);
    }
  };

  const handleBulkDeleteLineItemClick = async () => {
    if (selectedLineItemsIds.length === 0) {
      toast.error('No Line Items to delete. Please select one or more rows.');
      return;
    }

    setBulkDeleteInProgress(true);

    try {
      const isConfirmed = await getConfirmation({
        heading: `Delete ${selectedLineItemsIds.length} Line Item(s)`,
        description: 'Are you sure you want to delete these line items?',
        confirmButton: {
          label: 'Delete',
          color: 'error',
        },
        cancelButton: {
          label: 'Cancel',
          color: 'secondary',
        },
      });
      if (!isConfirmed) return;

      // Update local state
      const updatedDetailedDraws = detailedDraws.map(draw => {
        if (draw.projectDrawSummary.id === projectDrawId) {
          const { updatedSubtotals, reanchors } = computeSubtotalReanchors(
            new Set(selectedLineItemsIds),
            draw.subtotals ?? [],
            draw.lineItems ?? [],
          );
          return {
            ...draw,
            lineItems: draw.lineItems?.map(item => {
              if (selectedLineItemsIds.includes(item.id)) {
                return {
                  ...item,
                  isDeleted: true, // Set the isDeleted flag to true for selected items
                  isEdited: false,
                  isCreated: false,
                };
              }
              return item; // Return unmodified item
            }),
            subtotals: updatedSubtotals,
            pendingSubtotalReanchors:
              reanchors.length > 0 ? [...(draw.pendingSubtotalReanchors ?? []), ...reanchors] : draw.pendingSubtotalReanchors,
          };
        }
        return draw; // Return unmodified draw
      });

      setDetailedDraws(updatedDetailedDraws);

      // Update global state
      updateProject(
        {
          projectDraws: updatedDetailedDraws,
        },
        true,
      );
      setRowSelection({});
    } catch (error) {
      handleError(error);
    } finally {
      setBulkDeleteInProgress(false);
    }
  };

  // Filter line items to exclude those marked as deleted
  const filteredLineItems = React.useMemo(() => {
    return lineItems.filter(item => !item.isDeleted);
  }, [lineItems]);

  const numericColumnWidth = React.useMemo(() => {
    if (filteredLineItems.length === 0) return 92;
    const extractValues = (item: ProjectLineItemDTO) => [
      item.originalBudget || 0,
      item.reallocation || 0,
      item.changeOrder || 0,
      calculateRevisedBudget(item),
      item.previousApplication || 0,
      item.completeThisPeriod || 0,
      item.storedThisPeriod || 0,
      typeof item.cdccApproved === 'number' ? item.cdccApproved : calculateCdccApproved(item),
      calculateTotalCompletedStored(item),
      calculateBalanceToComplete(item),
      item.retainage || 0,
    ];
    const allValues = filteredLineItems.flatMap(extractValues);
    // Also include footer totals since they can be the widest rendered value
    allValues.push(
      filteredLineItems.reduce((acc, item) => acc + (item.originalBudget || 0), 0),
      filteredLineItems.reduce((acc, item) => acc + (item.reallocation || 0), 0),
      filteredLineItems.reduce((acc, item) => acc + (item.changeOrder || 0), 0),
      filteredLineItems.reduce((acc, item) => acc + (item.previousApplication || 0), 0),
      filteredLineItems.reduce((acc, item) => acc + (item.completeThisPeriod || 0), 0),
      filteredLineItems.reduce((acc, item) => acc + (item.storedThisPeriod || 0), 0),
      filteredLineItems.reduce((acc, item) => acc + (item.retainage || 0), 0),
    );
    const longestFormatted = allValues.reduce((longest, val) => {
      const f = formatCurrency(Math.round(val).toString(), false);
      return f.length > longest.length ? f : longest;
    }, '');
    // 7.5px per char is the rendered digit width; +18 covers the 6px body-cell padding each side.
    // The old +40 buffer existed to clear the column-actions button, which is now gone. The floor
    // is set by the longest single header word ("Reallocation") rather than by the data.
    const estimatedWidth = Math.ceil(longestFormatted.length * 7.5 + 18);
    return Math.min(Math.max(estimatedWidth, 84), 150);
  }, [filteredLineItems]);

  const totalsLineItem: ProjectLineItemDTO = React.useMemo(
    () => ({
      id: 'totals',
      lineItem: {
        id: 'totals',
        description: 'Totals',
        division: undefined,
        unit: '',
      },
      originalBudget: filteredLineItems.reduce((acc, item) => acc + (item.originalBudget || 0), 0),
      reallocation: filteredLineItems.reduce((acc, item) => acc + (item.reallocation || 0), 0),
      changeOrder: filteredLineItems.reduce((acc, item) => acc + (item.changeOrder || 0), 0),
      previousApplication: filteredLineItems.reduce((acc, item) => acc + (item.previousApplication || 0), 0),
      completeThisPeriod: filteredLineItems.reduce((acc, item) => acc + (item.completeThisPeriod || 0), 0),
      storedThisPeriod: filteredLineItems.reduce((acc, item) => acc + (item.storedThisPeriod || 0), 0),
      retainage: filteredLineItems.reduce((acc, item) => acc + (item.retainage || 0), 0),
      // following values are placeholders and will be auto-calculated at run-time
      revisedBudget: 0,
      cdccApproved: filteredLineItems.reduce(
        (acc, item) =>
          acc +
          // Prefer explicitly set cdccApproved; otherwise derive from component fields
          (typeof item.cdccApproved === 'number' ? item.cdccApproved : calculateCdccApproved(item)),
        0,
      ),
      completedStoredToDate: 0,
      percentageComplete: 0,
      balanceToComplete: 0,
      projectDrawId: '',
      projectDrawName: '',
      quantity: 0,
      isIncluded: true,
    }),
    [filteredLineItems],
  );

  // While a subtotal is in range-edit mode, render the live in-progress reorder (see
  // rangeEditor.previewLineItems/previewSubtotals in subtotal.util.tsx) instead of the last-saved
  // order, so checking/unchecking a row visibly moves it right away rather than only on Confirm.
  const displayLineItems = React.useMemo(
    () => (rangeEditor.previewLineItems ? rangeEditor.previewLineItems.filter(item => !item.isDeleted) : filteredLineItems),
    [rangeEditor.previewLineItems, filteredLineItems],
  );
  const displaySubtotals = rangeEditor.previewSubtotals ?? currentDrawSubtotals;
  const isSorted = sorting.length > 0;
  const showSortedSubtotalsNotice = isSorted && displaySubtotals.some(s => !s.isDeleted);

  // Interleave subtotal rows among the filtered line items, sorted by position. See
  // subtotal.util.tsx for details — subtotal values are always computed live, so this stays
  // correct as rows are edited without needing a refetch. Subtotals are positional, so they're
  // hidden while a column sort is active and come back once the sort is cleared.
  const displayData = React.useMemo(
    () =>
      isSorted
        ? displayLineItems
        : computeSubtotalDisplayData(displayLineItems, displayLineItems, displaySubtotals, projectDrawId, projectDrawName),
    [isSorted, displayLineItems, displaySubtotals, projectDrawId, projectDrawName],
  );

  const handleNumericCellChange = (cell: MRT_Cell<ProjectLineItemDTO>, event: React.ChangeEvent<any>) => {
    const nextValue = event.target.value;
    if (typeof nextValue !== 'string') return;

    if (!/^[\d$(),.\s-]*$/.test(nextValue)) {
      const row = cell.row as any;
      const cachedValue = row?._valuesCache?.[cell.column.id];
      const fallbackValue = cachedValue ?? cell.getValue<number>() ?? '';
      event.target.value = typeof fallbackValue === 'number' ? fallbackValue.toString() : String(fallbackValue ?? '');
    }
  };

  const handleSaveCell = (cell: MRT_Cell<ProjectLineItemDTO>, value: any) => {
    const updatedData = { ...cell.row.original };
    const originalRow = cell.row.original;

    if (cell.column.id === 'division') {
      updatedData.lineItem = {
        ...updatedData.lineItem,
        division: divisions.find(d => d.id === value) || undefined,
      };
    } else if (cell.column.id === 'lineItem.description') {
      // value is a LineItemDTO; also apply its unit and division to the row.
      const picked: LineItemDTO = value;
      updatedData.lineItem = {
        ...updatedData.lineItem,
        id: picked.id,
        description: picked.description,
        unit: picked.unit || updatedData.lineItem.unit,
        division: picked.divisionId ? (divisions.find(d => d.id === picked.divisionId) ?? updatedData.lineItem.division) : undefined,
      };
    } else {
      if (numericColumns.includes(cell.column.id)) {
        if (value === undefined || value === null) return false;
        if (typeof value === 'string') {
          const trimmedValue = value.trim();
          if (trimmedValue === '') {
            value = '0';
          } else {
            const cleanedValue = trimmedValue.replace(/[$,()\s:]/g, '');
            const isValidNumber = /^-?(?:\d+\.?\d*|\.\d+)$/.test(cleanedValue);
            if (!isValidNumber) return false;
          }
        }
      }
      const parsedValue = typeof value === 'string' && numericColumns.includes(cell.column.id) ? parseCurrency(value) : value;
      (updatedData as any)[cell.column.id] = parsedValue;

      if (cell.column.id === 'completeThisPeriod' || cell.column.id === 'storedThisPeriod') {
        const oldExpectedCdccApproved = (originalRow.completeThisPeriod || 0) + (originalRow.storedThisPeriod || 0);
        const currentCdccApproved = originalRow.cdccApproved ?? 0;
        const isCdccManuallyOverridden = Math.abs(currentCdccApproved - oldExpectedCdccApproved) > 0.01;

        if (!isCdccManuallyOverridden) {
          const newCompleteThisPeriod = cell.column.id === 'completeThisPeriod' ? parsedValue : originalRow.completeThisPeriod || 0;
          const newStoredThisPeriod = cell.column.id === 'storedThisPeriod' ? parsedValue : originalRow.storedThisPeriod || 0;
          updatedData.cdccApproved = newCompleteThisPeriod + newStoredThisPeriod;

          // Auto-update retainage based on new completedStoredToDate
          if (!originalRow.manualOverrides?.retainage) {
            const currentExpected = Math.round(calculateTotalCompletedStored(originalRow) * (retainagePercentage / 100));
            if (Math.round(originalRow.retainage || 0) !== currentExpected) {
              updatedData.manualOverrides = { ...originalRow.manualOverrides, retainage: true };
            } else {
              const newCompletedStoredToDate = (originalRow.previousApplication || 0) + updatedData.cdccApproved;
              updatedData.retainage = Math.round(newCompletedStoredToDate * (retainagePercentage / 100));
            }
          }
        }
      }

      if (cell.column.id === 'previousApplication') {
        if (!originalRow.manualOverrides?.retainage) {
          const currentCdcc = typeof originalRow.cdccApproved === 'number' ? originalRow.cdccApproved : calculateCdccApproved(originalRow);
          const newCompletedStoredToDate = parsedValue + currentCdcc;
          updatedData.retainage = Math.round(newCompletedStoredToDate * (retainagePercentage / 100));
        }
      }

      if (cell.column.id === 'cdccApproved') {
        if (!originalRow.manualOverrides?.retainage) {
          const currentExpected = Math.round(calculateTotalCompletedStored(originalRow) * (retainagePercentage / 100));
          if (Math.round(originalRow.retainage || 0) !== currentExpected) {
            updatedData.manualOverrides = { ...originalRow.manualOverrides, retainage: true };
          } else {
            const newCompletedStoredToDate = (originalRow.previousApplication || 0) + parsedValue;
            updatedData.retainage = Math.round(newCompletedStoredToDate * (retainagePercentage / 100));
          }
        }
      }

      if (cell.column.id === 'retainage') {
        const expectedRetainage = Math.round(calculateTotalCompletedStored(originalRow) * (retainagePercentage / 100));
        updatedData.manualOverrides = { ...originalRow.manualOverrides, retainage: (parsedValue || 0) !== expectedRetainage };
      }
    }
    processRowUpdate(updatedData, cell.row.original);
    return true;
  };

  const excel = useExcelEditing<ProjectLineItemDTO>({
    tableInstanceRef: tableRef,
    onSaveCell: handleSaveCell,
    excludedColumnIds: ['mrt-row-drag', 'mrt-row-select', 'mrt-row-actions', 'division', 'lineItem.description'],
    filteredRows: displayData,
    allFields: [...numericColumns, 'revisedBudget', 'completedStoredToDate', 'percentageComplete', 'balanceToComplete'],
  });

  // Why the grips are greyed out, if they are (the grip column is hidden entirely when readonly).
  const reorderDisabledReason = isSorted
    ? 'Clear the column sort to reorder rows'
    : rangeEditor.rangeEditSubtotalId
      ? 'Finish editing the subtotal first'
      : undefined;

  const handleReorder = React.useCallback(
    ({ lineItems: reorderedLineItems, rangeChanges }: RowReorderResult) => {
      const updatedDetailedDraws = detailedDraws.map(draw =>
        draw.projectDrawSummary.id === projectDrawId
          ? {
              ...draw,
              lineItems: reorderedLineItems,
              ...applySubtotalRangeChanges(draw.subtotals, draw.pendingSubtotalReanchors, rangeChanges),
            }
          : draw,
      );
      setDetailedDraws(updatedDetailedDraws);
      updateProject({ projectDraws: updatedDetailedDraws }, true);
    },
    [detailedDraws, projectDrawId, setDetailedDraws, updateProject],
  );

  const rowReorder = useRowReorderDrop({
    scope: projectDrawId,
    containerRef: excel.tableContainerRef,
    lineItems,
    subtotals: currentDrawSubtotals,
    rowSelection,
    disabled: Boolean(readonly) || !!reorderDisabledReason,
    onReorder: handleReorder,
  });
  const columns = React.useMemo<MRT_ColumnDef<ProjectLineItemDTO>[]>(() => {
    const getCalculatedBodyCellProps = (cell: MRT_Cell<ProjectLineItemDTO>) => ({
      ...excel.getBodyCellProps({ cell }),
      align: 'right' as const,
      sx: { backgroundColor: REPORT_COLORS.YELLOW },
    });

    const getDarkYellowBodyCellProps = (cell: MRT_Cell<ProjectLineItemDTO>) => ({
      ...excel.getBodyCellProps({ cell }),
      align: 'right' as const,
      sx: { backgroundColor: REPORT_COLORS.DARK_YELLOW },
    });

    const rightAlignedProps = {
      muiTableBodyCellProps: ({ cell }: { cell: MRT_Cell<ProjectLineItemDTO> }) => ({
        ...excel.getBodyCellProps({ cell }),
        align: 'right' as const,
      }),
      muiTableHeadCellProps: {
        align: 'right' as const,
        sx: (theme: any) => ({
          backgroundColor: theme.palette.primary.main,
          position: 'sticky',
          top: 0,
          color: '#ffffff',
          // Mirrors the compact type set in muiTableHeadCellProps — this per-column override
          // otherwise wins the merge and would leave the numeric headers at full size.
          padding: '6px 4px 6px 6px',
          fontSize: '0.7rem',
          fontWeight: 600,
          lineHeight: 1.2,
          border: 'none',
          justifyContent: 'center',
          whiteSpace: 'normal',
          '&:not(:last-of-type)::after': {
            content: '""',
            position: 'absolute',
            right: 0,
            top: '20%',
            height: '60%',
            width: '1px',
            backgroundColor: 'rgba(255, 255, 255, 0.15)',
            boxShadow: '0 0 4px rgba(255, 255, 255, 0.2)',
          },
          '& .Mui-TableHeadCell-Content-Wrapper': {
            justifyContent: 'flex-start',
            paddingRight: '2px',
            flexWrap: 'wrap',
            whiteSpace: 'normal',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          },
        }),
      },
      muiTableFooterCellProps: {
        align: 'right' as const,
      },
    };

    const baseColumns: Omit<MRT_ColumnDef<ProjectLineItemDTO>, 'size'>[] = [
      {
        accessorKey: 'lineItem.description',
        header: 'Description',
        Cell: ({ cell, row }) => <DescriptionCellRenderer cell={cell} row={row} />,
        Edit: ({ cell, table }) => <DescriptionCellEditor cell={cell} table={table} onCommit={picked => handleSaveCell(cell, picked)} />,
        Footer: () => 'Totals',
        enableEditing: (row: MRT_Row<ProjectLineItemDTO>) => !readonly && row.original.__rowType !== 'subtotal',
        muiTableBodyCellProps: ({ cell }) => excel.getBodyCellProps({ cell }),
      },
      {
        id: 'division',
        header: 'Division',
        accessorFn: row => row.lineItem.division?.code ?? '',
        Cell: ({ row }) => {
          const division = row.original.lineItem.division;
          return (
            <Box
              component='span'
              sx={{
                color: 'blue',
                fontStyle: division ? 'normal' : 'italic',
                cursor: 'pointer',
              }}
            >
              {division ? `${division.code} - ${division.name}` : 'Click to edit'}
            </Box>
          );
        },
        Edit: ({ cell, table }) => {
          const handleChange = (divisionId: string | null) => {
            handleSaveCell(cell, divisionId);
            table.setEditingCell(null);
          };

          return (
            <SelectDivisionFromGiven
              divisions={divisions}
              selectedDivision={cell.row.original.lineItem.division?.id ?? null}
              setSelectedDivision={handleChange}
            />
          );
        },
        enableEditing: (row: MRT_Row<ProjectLineItemDTO>) => !readonly && row.original.__rowType !== 'subtotal',
      },
      {
        accessorKey: 'originalBudget',
        header: 'Original Budget',
        enableEditing: (row: MRT_Row<ProjectLineItemDTO>) => !readonly && row.original.__rowType !== 'subtotal',
        muiEditTextFieldProps: ({ cell }) => ({
          sx: {
            '& .MuiInputBase-input': { textAlign: 'right' },
          },
          onChange: event => {
            handleNumericCellChange(cell, event);
          },
          ...excel.getEditTextFieldProps({ cell }),
        }),
        ...rightAlignedProps,
        Footer: () => formatCurrency(Math.round(totalsLineItem.originalBudget).toString(), false),
      },
      {
        accessorKey: 'reallocation',
        header: 'Reallocation',
        muiEditTextFieldProps: ({ cell }) => ({
          sx: {
            '& .MuiInputBase-input': { textAlign: 'right' },
          },
          onChange: event => {
            handleNumericCellChange(cell, event);
          },
          ...excel.getEditTextFieldProps({ cell }),
        }),
        ...rightAlignedProps,
        Footer: () => formatCurrency(totalsLineItem.reallocation.toString(), false, false, true),
      },
      {
        accessorKey: 'changeOrder',
        header: 'Change Order',
        muiEditTextFieldProps: ({ cell }) => ({
          sx: {
            '& .MuiInputBase-input': { textAlign: 'right' },
          },
          onChange: event => {
            handleNumericCellChange(cell, event);
          },
          ...excel.getEditTextFieldProps({ cell }),
        }),
        ...rightAlignedProps,
        Footer: () => formatCurrency(Math.round(totalsLineItem.changeOrder).toString(), false),
      },
      {
        accessorKey: 'revisedBudget',
        header: 'Revised Budget',
        Cell: ({ cell, row }) => {
          const noteContent = row.original.note?.noteContent?.find((n: any) => n.field === cell.column.id);
          const hasNote = !!noteContent && noteContent.payappValue !== noteContent.projectValue;
          return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', width: '100%' }}>
              <span>{formatCurrency(calculateRevisedBudget(row.original).toString(), false, true)}</span>
              {hasNote && (
                <Tooltip
                  title={
                    <div style={{ padding: 4 }}>
                      <div>
                        <strong>PayApp:</strong> {formatCurrency(noteContent.payappValue?.toString(), false, true)}
                      </div>
                      <div>
                        <strong>{noteContent.projectValue !== undefined ? 'Last Month' : 'System'}:</strong>{' '}
                        {formatCurrency((noteContent.projectValue ?? calculateRevisedBudget(row.original))?.toString(), false, true)}
                      </div>
                    </div>
                  }
                  arrow
                >
                  <Info sx={{ fontSize: 16, color: 'rgb(241, 161, 59)', ml: 1, flexShrink: 0 }} />
                </Tooltip>
              )}
            </div>
          );
        },
        ...rightAlignedProps,
        muiTableBodyCellProps: ({ cell }) => getCalculatedBodyCellProps(cell),
        enableEditing: false,
        Footer: () => formatCurrency(Math.round(calculateRevisedBudget(totalsLineItem)).toString(), false),
      },
      {
        accessorKey: 'previousApplication',
        header: 'Previous Application',
        Cell: ({ cell, row }) => {
          const noteContent = row.original.note?.noteContent?.find((n: any) => n.field === cell.column.id);
          const hasNote = !!noteContent && noteContent.payappValue !== noteContent.projectValue;
          return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', width: '100%' }}>
              <span>{formatCurrency(cell.getValue<number>()?.toString() ?? '0', false, true)}</span>
              {hasNote && (
                <Tooltip
                  title={
                    <div style={{ padding: 4 }}>
                      <div>
                        <strong>PayApp:</strong> {formatCurrency(noteContent.payappValue?.toString(), false, true)}
                      </div>
                      <div>
                        <strong>{noteContent.projectValue !== undefined ? 'Last Month' : 'System'}:</strong>{' '}
                        {formatCurrency((noteContent.projectValue ?? cell.getValue<number>())?.toString(), false, true)}
                      </div>
                    </div>
                  }
                  arrow
                >
                  <Info sx={{ fontSize: 16, color: 'rgb(241, 161, 59)', ml: 1, flexShrink: 0 }} />
                </Tooltip>
              )}
            </div>
          );
        },
        muiEditTextFieldProps: ({ cell }) => ({
          sx: {
            '& .MuiInputBase-input': { textAlign: 'right' },
          },
          onChange: event => {
            handleNumericCellChange(cell, event);
          },
          ...excel.getEditTextFieldProps({ cell }),
        }),
        ...rightAlignedProps,
        Footer: () => formatCurrency(Math.round(totalsLineItem.previousApplication).toString(), false),
      },
      {
        accessorKey: 'completeThisPeriod',
        header: 'Complete This Period',
        muiEditTextFieldProps: ({ cell }) => ({
          sx: {
            '& .MuiInputBase-input': { textAlign: 'right' },
          },
          onChange: event => {
            handleNumericCellChange(cell, event);
          },
          ...excel.getEditTextFieldProps({ cell }),
        }),
        ...rightAlignedProps,
        Footer: () => formatCurrency(Math.round(totalsLineItem.completeThisPeriod).toString(), false),
      },
      {
        accessorKey: 'storedThisPeriod',
        header: 'Stored This Period',
        muiEditTextFieldProps: ({ cell }) => ({
          sx: {
            '& .MuiInputBase-input': { textAlign: 'right' },
          },
          onChange: event => {
            handleNumericCellChange(cell, event);
          },
          ...excel.getEditTextFieldProps({ cell }),
        }),
        ...rightAlignedProps,
        Footer: () => formatCurrency(Math.round(totalsLineItem.storedThisPeriod).toString(), false),
      },
      {
        accessorKey: 'cdccApproved',
        header: 'CDCC Approved',
        muiEditTextFieldProps: ({ cell }) => ({
          sx: {
            '& .MuiInputBase-input': { textAlign: 'right' },
          },
          onChange: event => {
            handleNumericCellChange(cell, event);
          },
          ...excel.getEditTextFieldProps({ cell }),
        }),
        enableEditing: (row: MRT_Row<ProjectLineItemDTO>) => !readonly && row.original.__rowType !== 'subtotal',
        ...rightAlignedProps,
        muiTableBodyCellProps: ({ cell }) => getDarkYellowBodyCellProps(cell),
        Footer: () => formatCurrency(Math.round(totalsLineItem.cdccApproved).toString(), false),
      },
      {
        accessorKey: 'completedStoredToDate',
        header: 'Completed Stored To Date',
        Cell: ({ cell, row }) => {
          const noteContent = row.original.note?.noteContent?.find((n: any) => n.field === cell.column.id);
          const hasNote = !!noteContent && noteContent.payappValue !== noteContent.projectValue;
          return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', width: '100%' }}>
              <span>{formatCurrency(calculateTotalCompletedStored(row.original).toString(), false, true)}</span>
              {hasNote && (
                <Tooltip
                  title={
                    <div style={{ padding: 4 }}>
                      <div>
                        <strong>PayApp:</strong> {formatCurrency(noteContent.payappValue?.toString(), false, true)}
                      </div>
                      <div>
                        <strong>{noteContent.projectValue !== undefined ? 'Last Month' : 'System'}:</strong>{' '}
                        {formatCurrency((noteContent.projectValue ?? calculateTotalCompletedStored(row.original))?.toString(), false, true)}
                      </div>
                    </div>
                  }
                  arrow
                >
                  <Info sx={{ fontSize: 16, color: 'rgb(241, 161, 59)', ml: 1, flexShrink: 0 }} />
                </Tooltip>
              )}
            </div>
          );
        },
        ...rightAlignedProps,
        muiTableBodyCellProps: ({ cell }) => getCalculatedBodyCellProps(cell),
        enableEditing: false,
        Footer: () => formatCurrency(Math.round(calculateTotalCompletedStored(totalsLineItem)).toString(), false),
      },
      {
        accessorKey: 'percentageComplete',
        header: 'Percentage Complete',
        Cell: ({ row }) => formatPercentage(calculatePercentageComplete(row.original).toString()),
        ...rightAlignedProps,
        muiTableBodyCellProps: ({ cell }) => getCalculatedBodyCellProps(cell),
        enableEditing: false,
        Footer: () => formatPercentage(calculatePercentageComplete(totalsLineItem).toString()),
      },
      {
        accessorKey: 'balanceToComplete',
        header: 'Balance To Complete',
        Cell: ({ cell, row }) => {
          const noteContent = row.original.note?.noteContent?.find((n: any) => n.field === cell.column.id);
          const hasNote = !!noteContent && noteContent.payappValue !== noteContent.projectValue;
          return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', width: '100%' }}>
              <span>{formatCurrency(calculateBalanceToComplete(row.original).toString(), false, true)}</span>
              {hasNote && (
                <Tooltip
                  title={
                    <div style={{ padding: 4 }}>
                      <div>
                        <strong>PayApp:</strong> {formatCurrency(noteContent.payappValue?.toString(), false, true)}
                      </div>
                      <div>
                        <strong>{noteContent.projectValue !== undefined ? 'Last Month' : 'System'}:</strong>{' '}
                        {formatCurrency((noteContent.projectValue ?? calculateBalanceToComplete(row.original))?.toString(), false, true)}
                      </div>
                    </div>
                  }
                  arrow
                >
                  <Info sx={{ fontSize: 16, color: 'rgb(241, 161, 59)', ml: 1, flexShrink: 0 }} />
                </Tooltip>
              )}
            </div>
          );
        },
        ...rightAlignedProps,
        muiTableBodyCellProps: ({ cell }) => getCalculatedBodyCellProps(cell),
        enableEditing: false,
        Footer: () => formatCurrency(Math.round(calculateBalanceToComplete(totalsLineItem)).toString(), false),
      },
      {
        accessorKey: 'retainage',
        header: 'Retainage',
        muiEditTextFieldProps: ({ cell }) => ({
          sx: {
            '& .MuiInputBase-input': { textAlign: 'right' },
          },
          onChange: event => {
            handleNumericCellChange(cell, event);
          },
          ...excel.getEditTextFieldProps({ cell }),
        }),
        ...rightAlignedProps,
        muiTableBodyCellProps: ({ cell }) => getDarkYellowBodyCellProps(cell),
        Footer: () => formatCurrency(Math.round(totalsLineItem.retainage).toString(), false),
      },
    ];

    const extendedColumns = baseColumns.map(col => {
      const columnId = col.accessorKey ?? col.id ?? '';

      let size = numericColumnWidth;
      if (columnId === 'lineItem.description') {
        size = 210;
      } else if (columnId === 'division') {
        size = 200;
      }

      // Only apply custom Cell to numeric fields
      let Cell = col.Cell;
      if (numericColumns.includes(columnId)) {
        const shouldHighlightMismatch = columnId === 'retainage' || columnId === 'cdccApproved';
        const CellComponent = shouldHighlightMismatch ? renderColCell(true, retainagePercentage / 100) : renderColCell();
        Cell = CellComponent;
      }

      return {
        ...col,
        size,
        Cell,
      };
    });

    return extendedColumns;
  }, [readonly, divisions, retainagePercentage, totalsLineItem, excel, numericColumnWidth]);

  const renderHeader = (tableInstance: MRT_TableInstance<ProjectLineItemDTO>) => (
    <Box sx={{ width: '100%' }}>
      <Box mx={2} mb={0} display='flex' alignItems='center' justifyContent={'space-between'}>
        <Box display='flex' alignItems='center'>
          <List color='primary' sx={{ fontSize: '24px !important', fontWeight: '700 !important' }} />
          <Typography variant='h5' color='primary' sx={{ ml: 1, mb: 0 }}>
            Continuation Sheet
          </Typography>
        </Box>
        {!readonly && (
          <Stack direction={'row'} spacing={0} alignItems='center'>
            {payappDrawId && (
              <Stack direction='row' spacing={2} alignItems='center' px={2}>
                <LegendItem color={REPORT_COLORS.PAYAPP_ONLY_BACKGROUND_COLOR} label='Payapp only' />
                <LegendItem color={REPORT_COLORS.PROJECT_ONLY_BACKGROUND_COLOR} label='Last Month only' />
              </Stack>
            )}
            <Tooltip title='Retainage percentage applied to line items in this draw' arrow>
              <Box
                display='flex'
                alignItems='center'
                sx={{
                  mr: 2,
                  pl: 1.5,
                  pr: 1,
                  height: 36,
                  borderRadius: 20,
                  bgcolor: inputRetainagePercentageHasError ? 'error.light' : 'rgba(0, 0, 0, 0.06)',
                  '&:hover': { bgcolor: inputRetainagePercentageHasError ? 'error.light' : 'rgba(0, 0, 0, 0.1)' },
                  '&:has(input:focus)': { bgcolor: 'rgba(0, 0, 0, 0.1)' },
                }}
              >
                <Typography color='text.secondary' sx={{ mr: 1, whiteSpace: 'nowrap', fontWeight: 500, fontSize: '14px' }}>
                  Retainage
                </Typography>
                <TextField
                  placeholder='10'
                  variant='standard'
                  value={inputRetainagePercentageDraft}
                  onChange={e => setInputRetainagePercentageDraft(e.target.value)}
                  onBlur={() =>
                    onDrawInputRetainagePercentageChange?.('inputRetainagePercentage', inputRetainagePercentageDraft, 'drawSummary')
                  }
                  InputProps={{
                    disableUnderline: true,
                    inputComponent: NumericFormatInput as any,
                    endAdornment: (
                      <Typography variant='body2' color='text.secondary' sx={{ ml: 0.25 }}>
                        %
                      </Typography>
                    ),
                  }}
                  inputProps={{ style: { textAlign: 'right', padding: 0, fontSize: '0.95rem' } }}
                  sx={{ width: 40 }}
                />
              </Box>
            </Tooltip>
            <IconOverTextButton
              icon={<Search />}
              text='Search'
              tooltip='Search line items'
              onClick={() => setIsSearchOpen(!isSearchOpen)}
              sunken={isSearchOpen}
              showHoverEffect={true}
            />
            <IconOverTextButton
              icon={<Undo />}
              text='Undo'
              tooltip={undoHistory.length > 0 ? 'Undo last edit' : 'No edits to undo'}
              onClick={handleUndo}
              disabled={undoHistory.length === 0}
              showHoverEffect={true}
              wrapInSpan={true}
            />
            <Box
              sx={{
                ml: isSearchOpen ? 1 : 0,
                mr: 0,
                width: isSearchOpen ? '200px' : 0,
                transition: 'width 0.3s ease-in-out, opacity 0.3s ease-in-out, margin 0.3s ease-in-out',
                overflow: 'hidden',
                opacity: isSearchOpen ? 1 : 0,
              }}
            >
              <TextField
                placeholder='Search'
                size='medium'
                value={tableInstance.getState().globalFilter ?? ''}
                onChange={event => tableInstance.setGlobalFilter(event.target.value)}
                fullWidth
                InputProps={{ startAdornment: null }}
              />
            </Box>
            <IconOverTextButton
              icon={<AutoAwesome />}
              text='Auto Assign'
              tooltip='Click to predict divisions for unassigned line items using Artificial Intelligence'
              onClick={handleAutoAssignDivisions}
              loading={predictingDivisions}
              disabled={predictingDivisions || isLoading}
              showHoverEffect={true}
              wrapInSpan={true}
            />
            {monitoringType === 'Limited' && (
              <>
                {limitedReportId && onOpenExistingLimitedReport && (
                  <IconOverTextButton
                    icon={<Description />}
                    text='Open Report'
                    tooltip='Open existing limited report'
                    onClick={onOpenExistingLimitedReport}
                    showHoverEffect={true}
                  />
                )}
                {onCreateNewLimitedReport && (
                  <IconOverTextButton
                    icon={<AddIcon />}
                    text='New Report'
                    tooltip='Create new limited report'
                    onClick={onCreateNewLimitedReport}
                    showHoverEffect={true}
                  />
                )}
              </>
            )}
            {showMagicReport && monitoringType === 'Magic' && (
              <>
                {magicReportId && onOpenExistingMagicReport && (
                  <IconOverTextButton
                    icon={<Description />}
                    text='Open Report'
                    tooltip={magicReportAwaitingPayapp ? NO_PAYAPPS_UPLOADED_TOOLTIP : 'Open existing magic report'}
                    onClick={onOpenExistingMagicReport}
                    disabled={magicReportAwaitingPayapp}
                    showHoverEffect={true}
                    wrapInSpan={true}
                  />
                )}
                {onCreateNewMagicReport && (
                  <IconOverTextButton
                    icon={<AddIcon />}
                    text='New Report'
                    tooltip={magicReportAwaitingPayapp ? NO_PAYAPPS_UPLOADED_TOOLTIP : START_NEXT_REPORT_TOOLTIP}
                    onClick={onCreateNewMagicReport}
                    disabled={magicReportAwaitingPayapp}
                    showHoverEffect={true}
                    wrapInSpan={true}
                  />
                )}
              </>
            )}
            <IconOverTextButton
              icon={<AddIcon />}
              text='Add Line Item'
              tooltip='Add new line item'
              onClick={handleCreateLineItemClick}
              disabled={addingLineItem}
              showHoverEffect={true}
              wrapInSpan={true}
            />
            <IconOverTextButton
              icon={<PostAdd />}
              text='Add COs'
              tooltip='Add blank change order rows'
              onClick={() => setAddChangeOrdersDialogOpen(true)}
              disabled={addingChangeOrders}
              showHoverEffect={true}
              wrapInSpan={true}
            />
            {showPayappControls && !isMonitoringReport && (
              <IconOverTextButton
                icon={<CloudUpload />}
                text='Upload PayApp'
                tooltip='Upload PayApp document'
                onClick={openPayAppUploadDialog}
                showHoverEffect={true}
              />
            )}
            {showPayappControls && !isMonitoringReport && payappDraftId && (
              <IconOverTextButton
                icon={<Description />}
                text='Open PayApp'
                tooltip='Open PayApp draft'
                onClick={openPayAppDraft}
                showHoverEffect={true}
              />
            )}
            <IconOverTextButton
              icon={isFullScreen ? <FullscreenExit /> : <Fullscreen />}
              text={isFullScreen ? 'Exit Full' : 'Full Screen'}
              tooltip={isFullScreen ? 'Exit fullscreen mode' : 'Enter fullscreen mode'}
              onClick={() => setIsFullScreen(!isFullScreen)}
              showHoverEffect={true}
            />
          </Stack>
        )}
      </Box>
    </Box>
  );

  const table = useMaterialReactTable<ProjectLineItemDTO>({
    columns,
    data: displayData,
    initialState: {
      pagination: { pageIndex: 0, pageSize: 250 },
      showGlobalFilter: true,
      density: 'compact',
      columnPinning: { left: ['mrt-row-drag', 'mrt-row-select', 'lineItem.description', 'division'], right: ['mrt-row-actions'] },
      columnVisibility: isMonitoringReport ? { division: false } : {},
    },
    // Only for the grip column; dragging itself is handled by useRowReorderDrop.
    enableRowOrdering: !readonly,
    enableColumnFilters: false,
    // No per-column three-dot menu — it cost ~24px of every header, which is width the client
    // would rather spend on showing another column.
    enableColumnActions: false,
    layoutMode: 'grid',
    enableColumnResizing: true,
    enableColumnPinning: true,
    enableStickyFooter: true,
    columnResizeMode: 'onChange',
    enableRowSelection: (row: MRT_Row<ProjectLineItemDTO>) =>
      rangeEditor.rangeEditSubtotalId ? rangeEditor.poolIds.includes(row.original.id) : !readonly && row.original.__rowType !== 'subtotal',
    enableSelectAll: !rangeEditor.rangeEditSubtotalId,
    enableMultiRowSelection: true,
    enablePagination: true,
    positionPagination: 'none',
    muiPaginationProps: {
      rowsPerPageOptions: [250, 500, 1000],
      showFirstButton: true,
      showLastButton: true,
      sx: {
        marginBottom: 0,
        paddingBottom: 0,
        minHeight: '32px',
      },
    },
    enableDensityToggle: false,
    enableHiding: false,
    editDisplayMode: 'cell',
    enableEditing: !readonly, // or !readonly if conditional
    // Both checkboxes carry MUI's default 9px padding, which is what makes the select column look
    // wider than its content. Trimmed here rather than in the shared select-all helper, which
    // other tables use.
    muiSelectAllCheckboxProps: ({ table }) => {
      const selectAllProps = buildSelectAllCheckboxProps(table);
      return { ...selectAllProps, sx: { ...selectAllProps.sx, padding: '4px' } };
    },
    muiSelectCheckboxProps: { sx: { padding: '4px' } },
    onRowSelectionChange: handleRowSelectionChange,
    onColumnVisibilityChange: setColumnVisibilityModel,
    // Sorting would scramble the range-edit preview order, so it's off while a subtotal range is being edited.
    enableSorting: !rangeEditor.rangeEditSubtotalId,
    onSortingChange: getOnSortingChangeWithToast(sorting, setSorting),
    getRowId: row => row.id,
    positionActionsColumn: 'last',
    enableRowActions: !readonly,
    renderRowActions: ({ row }) => {
      if (row.original.__rowType === 'subtotal') {
        const isEditingThisRange = rangeEditor.rangeEditSubtotalId === row.original.id;
        const otherRangeEditActive = !!rangeEditor.rangeEditSubtotalId && !isEditingThisRange;

        if (isEditingThisRange) {
          // Confirm/Cancel stay pinned open — hiding an in-progress edit's only exit behind a
          // hover would be a trap.
          return (
            <HoverRowActions alwaysVisible>
              <Stack direction='row' spacing={1} alignItems='center' justifyContent='center'>
                <Tooltip title='Confirm Range' arrow={false}>
                  <WhiteFab
                    size='small'
                    sx={{ width: 32, height: 32, minHeight: 32 }}
                    icon={<Check fontSize='small' />}
                    textColor='primary'
                    onClick={() => rangeEditor.confirmRangeEdit()}
                  />
                </Tooltip>
                <Tooltip title='Cancel' arrow={false}>
                  <WhiteFab
                    size='small'
                    sx={{ width: 32, height: 32, minHeight: 32 }}
                    icon={<Close fontSize='small' />}
                    textColor='error'
                    onClick={() => rangeEditor.cancelRangeEdit()}
                  />
                </Tooltip>
              </Stack>
            </HoverRowActions>
          );
        }

        return (
          <HoverRowActions>
            <Stack direction='row' spacing={1} alignItems='center' justifyContent='center'>
              <Tooltip title='Edit Range' arrow={false}>
                <span>
                  <WhiteFab
                    size='small'
                    sx={{ width: 32, height: 32, minHeight: 32 }}
                    icon={<Edit fontSize='small' />}
                    textColor='primary'
                    onClick={() => {
                      setSorting([]);
                      rangeEditor.startRangeEdit(row.original.id);
                    }}
                    disabled={otherRangeEditActive}
                  />
                </span>
              </Tooltip>
              <Tooltip title='Delete Subtotal' arrow={false}>
                <span>
                  <WhiteFab
                    size='small'
                    sx={{ width: 32, height: 32, minHeight: 32 }}
                    icon={<Delete fontSize='small' />}
                    textColor='error'
                    onClick={() => handleDeleteSubtotalClick(row.original.id)}
                    disabled={otherRangeEditActive}
                  />
                </span>
              </Tooltip>
            </Stack>
          </HoverRowActions>
        );
      }

      // "Add Subtotal Below" anchors a new subtotal to this row. Only offered where it can actually
      // work: the anchor is a real, already-persisted line item (the create call is an immediate API
      // call that needs a real FK — see project.api.ts), and the row isn't already anchoring one.
      const rowAlreadyAnchorsSubtotal = currentDrawSubtotals.some(s => !s.isDeleted && s.anchorDrawLineItemId === row.original.id);
      const isUnsavedRow = row.original.id.startsWith('temp-');
      const addSubtotalDisabled = !!rangeEditor.rangeEditSubtotalId || rowAlreadyAnchorsSubtotal || isUnsavedRow;

      return (
        <HoverRowActions>
          <Stack direction='row' spacing={1} alignItems='center' justifyContent='center'>
            <Tooltip
              title={
                isUnsavedRow
                  ? 'Save this line item before adding a subtotal below it'
                  : rowAlreadyAnchorsSubtotal
                    ? 'This line item already has a subtotal below it'
                    : 'Add Subtotal Below'
              }
              arrow={false}
            >
              <span>
                <WhiteFab
                  size='small'
                  sx={{ width: 32, height: 32, minHeight: 32 }}
                  icon={<Functions fontSize='small' />}
                  textColor='primary'
                  onClick={() => handleInsertSubtotalClick(row.original)}
                  disabled={addSubtotalDisabled}
                />
              </span>
            </Tooltip>
            {onToggleLineItemHighlight && (
              <Tooltip title={highlightedLineItemIds?.includes(row.original.id) ? 'Remove Highlight' : 'Highlight Line Item'} arrow={false}>
                <span>
                  <WhiteFab
                    size='small'
                    sx={{ width: 32, height: 32, minHeight: 32 }}
                    icon={highlightedLineItemIds?.includes(row.original.id) ? <Star fontSize='small' /> : <StarBorder fontSize='small' />}
                    textColor='primary'
                    onClick={() => onToggleLineItemHighlight(row.original.id)}
                    disabled={!!rangeEditor.rangeEditSubtotalId}
                  />
                </span>
              </Tooltip>
            )}
            <Tooltip title='Edit Line Item' arrow={false}>
              <span>
                <WhiteFab
                  size='small'
                  sx={{ width: 32, height: 32, minHeight: 32 }}
                  icon={<Edit fontSize='small' />}
                  textColor='primary'
                  onClick={() => handleUpdateLineItemClick(row.original)}
                  disabled={!!rangeEditor.rangeEditSubtotalId}
                />
              </span>
            </Tooltip>
            <Tooltip title='Delete Line Item' arrow={false}>
              <span>
                <WhiteFab
                  size='small'
                  sx={{ width: 32, height: 32, minHeight: 32 }}
                  icon={<Delete fontSize='small' />}
                  textColor='error'
                  onClick={() => handleDeleteLineItemClick(row.original)}
                  disabled={!!rangeEditor.rangeEditSubtotalId}
                />
              </span>
            </Tooltip>
          </Stack>
        </HoverRowActions>
      );
    },
    displayColumnDefOptions: {
      'mrt-row-drag': {
        header: '',
        size: 28,
        muiTableBodyCellProps: { sx: { border: 'none', padding: '0 2px' } },
        Cell: ({ row }) => {
          const { rowIds, disabledReason } = rowReorder.getRowDrag(row.id);
          return (
            <RowDragHandle
              scope={projectDrawId}
              rowIds={reorderDisabledReason ? null : rowIds}
              disabledReason={reorderDisabledReason ?? disabledReason}
            />
          );
        },
      },
      // MRT defaults this column to 60px for a 20px checkbox. 40 leaves 36px of content box after
      // the cell padding below — enough for the 24px select-all icon plus its trimmed padding.
      'mrt-row-select': {
        size: 40,
        muiTableBodyCellProps: { sx: { border: 'none', padding: '0 2px' } },
        // A column-level `sx` REPLACES the table-level one (MRT shallow-merges these prop objects),
        // so the header styling has to be restated here — otherwise this cell loses the primary
        // background and renders white.
        muiTableHeadCellProps: {
          sx: (theme: any) => ({
            backgroundColor: theme.palette.primary.main + ' !important',
            color: '#ffffff',
            justifyContent: 'center',
            padding: '0 2px',
            border: 'none',
            // This column is pinned left; without the pseudo-element fill the body scrolls
            // through behind the sticky header.
            '&[data-pinned="true"]:not([role="columnheader"])::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: 0,
              right: 0,
              backgroundColor: theme.palette.primary.main,
              zIndex: 0,
            },
            '&[data-pinned="true"]:not([role="columnheader"])': {
              zIndex: 1,
            },
          }),
        },
      },
      'mrt-row-actions': {
        header: '', // optional: remove default text
        size: ACTIONS_COLUMN_WIDTH,
        ...getActionsColumnStyling(),
        // MRT v3 hard-sets `overflow: hidden` on every cell, which clips the hover pill down to
        // the 44px the column is wide. It has to be lifted here on the column def: MRT does not
        // emit data-column-id, so a `td[data-column-id=...]` selector can't reach these cells.
        // The z-index raise puts the pill above the data cells it floats over.
        muiTableBodyCellProps: {
          // Restates border/padding for the same shallow-merge reason as mrt-row-select above.
          sx: { border: 'none', padding: '3px 6px', overflow: 'visible !important', zIndex: 4 },
        },
        // Fixed-width strip; resizing it would defeat the point, and dropping the resize handle
        // also removes an in-flow sibling from the header content row (see below).
        enableResizing: false,
        muiTableHeadCellProps: {
          ...getActionsColumnStyling().muiTableHeadCellProps,
          sx: {
            ...getActionsColumnStyling().muiTableHeadCellProps.sx,
            overflow: 'visible !important',
            zIndex: 11,
            // MRT nests the header element in Content > Content-Labels > Content-Wrapper, all of
            // which shrink-wrap their content. Without this the pill's anchor box is a narrow
            // box at flex-start rather than the full cell, so `right` is measured from the wrong
            // edge and the pill lands ~20px left of the row pills.
            '& .Mui-TableHeadCell-Content, & .Mui-TableHeadCell-Content-Labels, & .Mui-TableHeadCell-Content-Wrapper': {
              width: '100%',
            },
          },
        },
        // The bulk buttons only work with a selection, so they stay out of the way until there is
        // one — and then float leftwards like the row actions rather than widening the column.
        Header: ({ table }) => {
          const hasSelection = table.getSelectedRowModel().flatRows.length > 0;
          return (
            <Box sx={{ position: 'relative', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {!hasSelection && (
                <Tooltip title='Select line items to enable bulk edit and delete' arrow={false}>
                  {/* The header's Content-Labels wrapper sets pointerEvents: none to kill sorting
                      clicks, which would also swallow this tooltip's hover — re-enable it here. */}
                  <Box
                    component='span'
                    sx={{ display: 'inline-flex', pointerEvents: 'auto', cursor: 'default' }}
                    onClick={event => event.stopPropagation()}
                  >
                    <MoreVert fontSize='small' sx={{ color: 'rgba(255, 255, 255, 0.65)' }} />
                  </Box>
                </Tooltip>
              )}
              {hasSelection && (
                <Box sx={revealPillSx(HEADER_PILL_RIGHT_OFFSET)}>
                  <Stack direction='row' spacing={1} alignItems='center' justifyContent='flex-end'>
                    <Tooltip title='Edit Selected Line Items' arrow={false}>
                      <span>
                        <WhiteFab
                          size='small'
                          sx={{ width: 32, height: 32, minHeight: 32 }}
                          icon={<Edit fontSize='small' />}
                          textColor='primary'
                          onClick={() => setBulkEditDialogOpen(true)}
                          loading={bulkEditInProgress}
                        />
                      </span>
                    </Tooltip>
                    <Tooltip title='Delete Selected Line Items' arrow={false}>
                      <span>
                        <WhiteFab
                          size='small'
                          sx={{ width: 32, height: 32, minHeight: 32 }}
                          icon={<Delete fontSize='small' />}
                          textColor='error'
                          onClick={handleBulkDeleteLineItemClick}
                          loading={bulkDeleteInProgress}
                        />
                      </span>
                    </Tooltip>
                  </Stack>
                </Box>
              )}
            </Box>
          );
        },
      },
    },
    muiTableProps: {
      sx: {
        marginTop: '0px',
        minWidth: 'min-content',
      },
    },
    muiTablePaperProps: {
      sx: {
        boxShadow: 'none',
        backgroundColor: 'white',
      },
    },
    muiTableContainerProps: {
      ...excel.getContainerProps(),
      onKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => {
        if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'z' && !event.shiftKey) {
          const editingCell = tableRef.current?.getState().editingCell;
          if (!editingCell) {
            event.preventDefault();
            handleUndo();
            return;
          }
        }
        excel.getContainerProps().onKeyDown?.(event);
      },
      sx: {
        ...excelSelectedCellSx,
        padding: '0px',
        height: isFullScreen ? 'calc(100vh - 110px)' : 'auto',
        maxHeight: isFullScreen ? 'calc(100vh - 110px)' : (scrollHeight ?? 'none'),
        overflowX: 'scroll !important' as any,
        overflowY: isFullScreen ? 'auto' : scrollHeight ? 'auto' : 'visible',
        '&::-webkit-scrollbar': {
          width: '12px',
          height: '12px',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: '#c1c1c1',
          borderRadius: '6px',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: '#f1f1f1',
        },
        // Hide the gray empty row
        '& .MuiBox-root.css-kd8zx9': {
          display: 'none !important',
        },
        // Override MRT's pinned cell alignment to center
        '& .css-1jh1yab-MuiTableCell-root[data-pinned="true"]': {
          justifyContent: 'center !important',
        },
        '& .css-1evnjx0-MuiTableCell-root': {
          justifyContent: 'center !important',
        },
        // Ensure table body has enough height to push footer down
        '& .MuiTableBody-root': {
          minHeight: '300px',
          display: 'unset',
        },
        // Click-to-sort is bound to .Mui-TableHeadCell-Content-Labels. It used to be enabled only
        // incidentally, by the `th:has(button)` rule that matched because every header carried the
        // column-actions button — so switching that button off killed header sorting outright.
        // Enable it explicitly instead of depending on what else happens to be in the header.
        '& .Mui-TableHeadCell-Content-Labels': { pointerEvents: 'auto', cursor: 'pointer' },
        // Hides the sort-direction arrow, which MRT wraps in a Badge and sizes at 3ch — i.e. ~24px
        // of every header. Pre-existing, and kept deliberately: that width is what this work is
        // trying to reclaim.
        '& .Mui-TableHeadCell-Content-Labels .MuiBadge-root': { display: 'none' },
      },
    },
    muiTableBodyRowProps: ({ row }) => {
      const isRangeChecked = !!rangeEditor.rangeEditSubtotalId && rangeEditor.checkedIds.has(row.original.id);
      const isActiveEditingSubtotal = rangeEditor.rangeEditSubtotalId === row.original.id;

      if (row.original?.__rowType === 'subtotal') {
        const subtotalBackgroundColor = SUBTOTAL_ROW_BACKGROUND_COLOR;
        return {
          ...getReorderRowAttributes(row.original),
          sx: {
            opacity: rowReorder.isRowDragged(row.id) ? 0.5 : 1,
            cursor: 'default',
            backgroundColor: subtotalBackgroundColor + ' !important',
            border: isActiveEditingSubtotal ? '2px solid #1976d2' : 'none',
            fontWeight: 700,
            '& td[data-pinned="true"]::before': {
              backgroundColor: subtotalBackgroundColor,
            },
          },
        };
      }

      const isHighlighted = !!highlightedLineItemIds?.includes(row.original.id);
      let backgroundColor = '';

      if (isRangeChecked) {
        // Highlights the line items currently claimed by the subtotal being range-edited.
        backgroundColor = '#DCEEFB';
      } else if (isHighlighted) {
        backgroundColor = REPORT_COLORS.HIGHLIGHT_BACKGROUND_COLOR;
      } else if (payappMergedDraw) {
        if (row.original?.isCreated) {
          backgroundColor = REPORT_COLORS.PAYAPP_ONLY_BACKGROUND_COLOR;
        } else if (row.original?.isEdited && row.original?.exitingAndNotMatched) {
          backgroundColor = REPORT_COLORS.PROJECT_ONLY_BACKGROUND_COLOR;
        }
      } else {
        if (row.original?.isEdited || row.original?.isCreated) {
          backgroundColor = REPORT_COLORS.UNSAVED_BACKGROUND_COLOR;
        }
      }

      // Fallback to zebra striping if no custom background
      if (!backgroundColor) {
        backgroundColor = row.index % 2 === 0 ? '#f9f9f9' : '#ffffff';
      }

      return {
        ...getReorderRowAttributes(row.original),
        onClick: () => handleRowClick(row),
        sx: {
          opacity: rowReorder.isRowDragged(row.id) ? 0.5 : 1,
          cursor: 'pointer',
          backgroundColor: backgroundColor + ' !important',
          border: 'none',
          // Also apply the same background to sticky pseudo-elements in this row
          '& td[data-pinned="true"]::before': {
            backgroundColor: backgroundColor,
          },
        },
      };
    },
    state: {
      isFullScreen,
      rowSelection: rangeEditor.rangeEditSubtotalId ? rangeEditSelectionState : rowSelection,
      columnVisibility: columnVisibilityModel,
      sorting,
      showGlobalFilter: !isFullScreen,
      showColumnFilters: false,
    },
    muiTableHeadProps: {
      sx: {
        position: 'sticky',
        top: 0,
        zIndex: 2,
        padding: 0,
        color: 'white',
      },
    },
    muiTableHeadCellProps: {
      sx: theme => ({
        backgroundColor: theme.palette.primary.main + ' !important',
        color: '#ffffff',
        justifyContent: 'center',
        // Compact header type + padding so a column can be as narrow as its data needs.
        padding: '6px 6px',
        fontSize: '0.7rem',
        fontWeight: 600,
        lineHeight: 1.2,
        border: 'none',
        '&[data-pinned="true"]:not([role="columnheader"])::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: theme => theme.palette.primary.main,
          zIndex: 0,
        },
        '&[data-pinned="true"]:not([role="columnheader"])': {
          zIndex: 1,
        },
        '&:not(:last-of-type)::after': {
          content: '""',
          position: 'absolute',
          right: 0,
          top: '20%',
          height: '60%',
          width: '1px',
          backgroundColor: 'rgba(255, 255, 255, 0.15)',
          boxShadow: '0 0 4px rgba(255, 255, 255, 0.2)',
        },
        '& .Mui-TableHeadCell-Content-Wrapper': {
          flexWrap: 'wrap',
          whiteSpace: 'normal',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        },
        '& .Mui-TableHeadCell-ResizeHandleWrapper': {
          right: 0,
          transform: 'translateX(50%)',
        },
      }),
    },
    muiTableBodyCellProps: {
      sx: {
        border: 'none',
        padding: '3px 6px',
        // Override alignment for actions column specifically
        '&[data-column-id="mrt-row-actions"]': {
          textAlign: 'center !important',
          '& .MuiStack-root': {
            justifyContent: 'center !important',
          },
        },
      },
    },
    muiTableFooterCellProps: {
      sx: theme => ({
        backgroundColor: theme.palette.primary.main,
        color: '#ffffff',
        fontWeight: 'bold',
        // Bold digits are wider than the body text the column was sized against, so the totals
        // row needs the smaller size to stay inside the narrowed columns.
        fontSize: '0.78rem',
        padding: '10px 6px',
        border: 'none',

        // Sticky footer cell background via ::before
        '&[data-pinned="true"]::before': {
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: theme.palette.primary.main,
          zIndex: 0,
        },

        // Maintain stickiness & layer above ::before
        '&[data-pinned="true"]': {
          zIndex: 1,
        },

        '&:not(:last-of-type)::after': {
          content: '""',
          position: 'absolute',
          right: 0,
          top: '20%',
          height: '60%',
          width: '1px',
          backgroundColor: 'rgba(255, 255, 255, 0.15)',
          boxShadow: '0 0 4px rgba(255, 255, 255, 0.2)',
        },
      }),
    },

    muiTopToolbarProps: {
      sx: {
        backgroundColor: 'white !important',
        justifyContent: 'flex-end !important',
        display: 'flex',
        px: 0,
        py: 0,
        minHeight: 'auto',
        '& .MuiToolbar-root': {
          padding: 0,
          width: '100%',
          justifyContent: 'flex-end',
          minHeight: '36px',
        },
        // Hide any built-in column actions button
        '& .MuiButton-root[aria-label*="column"]': {
          display: 'none !important',
        },
        '& .MuiButton-root[aria-label*="Column"]': {
          display: 'none !important',
        },
        '& button[aria-label*="Hide"]': {
          display: 'none !important',
        },
        '& button[aria-label*="Show"]': {
          display: 'none !important',
        },
        '& .MuiTablePagination-root': {
          marginBottom: 0,
          paddingBottom: 0,
          minHeight: '32px',
        },
      },
    },
    renderTopToolbarCustomActions: isFullScreen
      ? ({ table }) => (
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
              px: 0,
              py: 0,
              minHeight: '36px',
              gap: 0,
            }}
          >
            <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-end', margin: 0 }}>
              {renderHeader(table as MRT_TableInstance<ProjectLineItemDTO>)}
            </Box>
          </Box>
        )
      : undefined,
    renderBottomToolbar: ({ table }) => (
      <Stack
        p={0}
        m={0}
        direction={'row'}
        alignItems={'center'}
        justifyContent={'flex-end'}
        sx={{
          width: '100%',
          backgroundColor: 'white',
          px: 2,
          py: 1,
          borderTop: '1px solid #eee',
          '& .MuiTablePagination-root': { m: 0, p: 0, minHeight: '32px' },
        }}
      >
        <MRT_TablePagination table={table} />
      </Stack>
    ),
    // Enable toolbar only in fullscreen for custom header
    enableTopToolbar: isFullScreen,
    enableFullScreenToggle: false,
    // Control fullscreen state so we can react in our layout
    onIsFullScreenChange: setIsFullScreen,
  });
  tableRef.current = table;
  useScrollToRow(table, excel.tableContainerRef, scrollToRowId, () => setScrollToRowId(null));

  return (
    <Card
      elevation={0}
      sx={{
        marginTop: 1,
        marginBottom: 2,
        paddingTop: isFullScreen ? 0 : '16px',
        minHeight: '400px',
      }}
    >
      {/* Header row - show above table when not fullscreen */}
      {!isFullScreen && renderHeader(table)}
      {showSortedSubtotalsNotice && (
        <Box sx={{ px: 2, pb: 1, display: 'flex', alignItems: 'center', gap: 0.75, color: 'info.main' }}>
          <InfoOutlined fontSize='small' />
          <Typography variant='body2' sx={{ color: 'text.secondary' }}>
            Subtotals are hidden while the table is sorted. Clear the column sort to see them again.
          </Typography>
        </Box>
      )}
      <Box sx={{ overflowX: 'auto', px: 2, minHeight: '300px' }}>
        <Box sx={{ minWidth: '100%' }}>
          <MaterialReactTable table={table} />
        </Box>
      </Box>
      {rowReorder.dropIndicator}
      <EditDrawLineItemDialog
        open={editDrawLineItemDialogOpen}
        onClose={() => setEditDrawLineItemDialogOpen(false)}
        projectDrawId={projectDrawId}
        lineItem={selectedLineItem}
        setValues={handleCreateOrEditLineItem}
      />
      <SubtotalLabelDialog
        open={!!subtotalDialogState}
        title='Add Subtotal'
        initialLabel=''
        saving={subtotalDialogSaving}
        onClose={handleSubtotalDialogClose}
        onSubmit={handleSubtotalDialogSubmit}
      />
      <AddChangeOrdersDialog
        open={addChangeOrdersDialogOpen}
        onClose={() => setAddChangeOrdersDialogOpen(false)}
        onConfirm={handleAddChangeOrders}
        loading={addingChangeOrders}
        previewDescriptions={previewChangeOrderDescriptions}
      />
      <BulkEditDrawLineItemsDialog
        open={bulkEditDialogOpen}
        onClose={() => setBulkEditDialogOpen(false)}
        onSave={handleBulkEditLineItems}
        isUnitEditable={false}
        loading={bulkEditInProgress}
      />
      <UploadDialog
        open={payAppUploadDialogOpen}
        onClose={closePayAppUploadDialog}
        filesToAccept={{ 'application/pdf': ['.pdf'] }} // Only PDF files
        projectId={projectId}
        projectNumber={projectNumber}
        projectName={projectName}
        payappDrawId={projectDrawId}
        projectDraw={detailedDraws.find(draw => draw.projectDrawSummary.id === projectDrawId)}
        divisions={divisions}
        hideTabs={true}
        defaultTab={1}
      />
    </Card>
  );
}

export default ProjectTable;
